import {
    driveRepository,
    eventRepository,
    fragmentRepository,
    gameRepository,
    playRepository,
    statsRepository,
    transactionManager
} from "../storage/storage";
import PersistenceError from "./exceptions/persistence-error";
import FootballRuleSetProcessor from "./ruleset-processors/football-ruleset-processor";
import FootballPlay from "../sports/football/play/football-play";
import ContextService from "./context-service";
import FootballStatsService from "./football-stats-service";
import TransitionBuilder from "../sports/football/state/transition-builder";
import TeamService from "./team-service";
import EventService from "./event-service";
import WizardService from "./wizard-service";
import GameService from "./game-service";
import ValidationService from "./validation-service";
import PlayerService from "./player-service";
import DriveService from "./drive-service";
import FootballStringifier from "./stringifiers/football-stringifier";
import FragmentFactory from "./factories/fragment-factory";
import { getSafeOrDefault, isArray, isNumber } from "./common/object-util";
import { sanitizeInvalidCharacters } from "./common/string-util";
import { EventStatusCode } from "../sports/common/event-status";
import DriveEndType, {
    KICKOFF_DRIVE_END_TYPES
} from "../sports/football/enums/drive-end-type";
import GamePeriodChangeError from "./exceptions/game-period-change-error";
import FootballState from "../sports/football/state/football-state";
import FootballTransition from "../sports/football/state/football-transition";
import FootballTransitionConfig from "../sports/football/state/football-play-transitions";
import GamedayApiService from "./gameday-api-service";
import FootballSerializer from "./serializers/FootballSerializer";
import {
    getPeriodStartSeconds,
    isBarBallEligible,
    isCflKickEligible,
    isDeuceEligible,
    isFieldGoalInelligible,
    isOvertimePeriod,
    isPuntAllowed,
    isReboundNetEligible,
    isRouge,
    isSafety,
    secondHalfPeriodNumber,
    shouldHideEop
} from "../sports/football/utils/context-utils";
import RecoversPossessionSetting from "../sports/football/state/recovers-possession-setting";
import FirstDownStyles from "../sports/football/enums/first-down-styles";
import FootballStatsCategory from "../sports/football/stats/football-stats-category";
import PatCompleteStats from "../sports/football/rules/general/scoring/pat-complete-stats";
import FootballBoxscoreService from "./boxscores/football-boxscore-service";
import TeamPlayer from "../sports/common/players/TeamPlayer";
import NoPlayer, { NONE_UNIFORM } from "../sports/common/players/NoPlayer";
import FragmentDataType from "../sports/football/enums/fragment-data-type";
import { ManualGameClock } from "../storage";
import ClockFactory from "../sports/common/utils/clock-factory";
import { recoverUpCountingClockData } from "../sports/football/utils/up-clock-recovery";
import PlayInsertionType from "../sports/football/state/PlayInsertionType";
import Fragment from "../sports/football/fragments/fragment";
import CollectDriveTime from "../sports/football/fragments/collect-drive-time";
import StreamService from "./stream-service";
import ParticipationStyle from "../sports/football/enums/participation-style";
import Ruleset from "../sports/football/enums/ruleset";

export class FootballService {
    constructor() {
        this.ruleSetProcessor = FootballRuleSetProcessor;
    }

    async savePlay(play) {
        try {
            const saved = await transactionManager.transaction(
                "rw",
                [playRepository, fragmentRepository],
                async () => {
                    if (!play.index) {
                        const playCount = await playRepository
                            .where({ gameId: play.gameId })
                            .count();

                        play.index = playCount + 1;
                    }
                    play.id = await playRepository.put(play);
                    play.fragments.forEach((f) => {
                        f.playId = play.id;
                    });
                    await fragmentRepository.bulkPut(play.fragments);

                    return this.getPlay(play.id);
                }
            );

            return saved;
        } catch (err) {
            throw new PersistenceError(err);
        }
    }

    /**
     *
     * @param {string} gameId
     * @param {number} index
     * @returns {Promise<FootballPlay | null>}
     */
    async _getPlayAtIndex(gameId, index) {
        return playRepository.where({ gameId: gameId, index: index }).first();
    }

    async getPlay(id) {
        return playRepository.getPlayWithFragments(id);
    }

    async _updateContextToPreviousSnapshot(playId) {
        const play = await this.getPlay(playId);

        const gameContext = await ContextService.getGameContext(play.gameId);
        const { _type } = gameContext;

        return ContextService.updateContext(
            Object.assign(gameContext, { ...play.getContextSnapshot(), _type })
        );
    }

    async _deletePlayAndUpdateDrive(id) {
        const playToDelete = await this.getPlay(id);
        const gameId = playToDelete.gameId;
        const driveId = playToDelete.driveId;

        await this.deletePlay(id);
        await this._reconcileDriveAfterDeletion(gameId, driveId, playToDelete);
        await this._pruneEmptyDrives(gameId);
    }

    async _deleteLastPlay(id) {
        const gameContext = await this._updateContextToPreviousSnapshot(id);
        const playToDelete = await this.getPlay(id);
        const gameId = playToDelete.gameId;
        const driveId = playToDelete.driveId;
        const { eventId } = await gameRepository.getById(gameId);

        await this.deletePlay(id);
        await StreamService.updateMarkerPlayIdRefs(eventId, id, null);
        await this._reconcileDriveAfterDeletion(gameId, driveId, playToDelete);
        await this._pruneEmptyDrives(gameId);

        return FootballSerializer.serializeGameContext(gameContext);
    }

    /**
     * Keep drives consistent after a play is deleted. A drive exists only
     * while it has at least one play, so:
     *  - no plays remain → delete the drive (and shift later drive indexes);
     *  - the drive-ending play was removed → reopen the drive (unless the
     *    removed play was a trailing PAT/penalty, which never ends a drive).
     * (The drive's start fields are fixed at creation time and do not depend
     * on which plays remain, so removing a non-ending play needs no fixup.)
     * @param {string} gameId
     * @param {number} driveId
     * @param {FootballPlay} playToDelete
     * @private
     */
    async _reconcileDriveAfterDeletion(gameId, driveId, playToDelete) {
        if (!driveId) {
            return;
        }
        const drive = await DriveService.getById(driveId);
        if (!drive) {
            return;
        }

        const remaining = await playRepository
            .where({ driveId: driveId })
            .toArray();

        if (!remaining.length) {
            const lastDrive = await DriveService.getLastDrive(gameId);
            if (lastDrive && drive.index < lastDrive.index) {
                await this._propagateDriveIndexes(gameId, drive.index, {
                    decrement: true,
                    future: true
                });
            }
            return DriveService.deleteDrive(gameId, driveId);
        }

        const lastIndex = Math.max(...remaining.map((p) => p.index));
        const isPATPlay = playToDelete.historyContains(FootballState.PatTry);
        const isPenaltyPlay = playToDelete.historyContains(
            FootballState.PlayPenalty
        );

        if (playToDelete.index > lastIndex && !isPATPlay && !isPenaltyPlay) {
            await DriveService.openDrive(driveId);
        }
    }

    /**
     * Remove drives that no longer reference any play. With Start of Drive
     * plays gone, a drive is opened eagerly when a possession ends; deleting
     * that possession-ending play before the next drive records a play leaves
     * the freshly-opened drive empty, so it must be cleaned up.
     * @param {string} gameId
     * @private
     */
    async _pruneEmptyDrives(gameId) {
        const drives = await DriveService.getByGameId(gameId);
        const empties = drives
            .filter((d) => !(d.plays || []).length)
            .sort((a, b) => b.index - a.index);

        for (const drive of empties) {
            const lastDrive = await DriveService.getLastDrive(gameId);
            if (lastDrive && drive.index < lastDrive.index) {
                await this._propagateDriveIndexes(gameId, drive.index, {
                    decrement: true,
                    future: true
                });
            }
            await DriveService.deleteDrive(gameId, drive.id);
        }
    }

    /**
     * Retrieve the most relevant driveId for a given play, if none is found, then recursively search for the next driveId that exists, for use during play reintegration.
     * @param {FootballPlay} play
     * @returns {Promise<{driveId: number | null, index: number}>} An object containing the driveId that should be used for reintegration and the index of the last play that was checked.
     * @private
     */
    async _retrieveReintegrationDriveId(play) {
        const nextPlay = await this._getNextPlay(play);
        const parsedDriveId =
            play.driveId || play.contextSnapshot.currentDriveId || null;

        if (!parsedDriveId && nextPlay && nextPlay.isComplete()) {
            return this._retrieveReintegrationDriveId(nextPlay);
        }
        return { index: play.index, driveId: parsedDriveId };
    }

    /**
     * Copy the contextSnapshot from the provided play to the next play
     * @param play
     * @returns {Promise<*|null>}
     * @private
     */
    async _carryOverContextSnapshot(play) {
        const { contextSnapshot } = play;
        const nextPlay = await this._getNextPlay(play);
        if (nextPlay) {
            return playRepository.put(
                Object.assign(nextPlay, { contextSnapshot })
            );
        }
        return null;
    }

    /**
     * Delete a play, replace the next sequential play's contextSnapshot and reintegrate a range of plays.
     * @param {string} id
     * @returns {Promise<{FootballGameContext}>}
     * @private
     */
    async _deleteHistoricalPlay(id) {
        const playToDelete = await this.getPlay(id);

        // Detect (before mutating snapshots) whether this play is a turnover:
        // its starting possession differs from the following play's. Deleting
        // such a play dissolves the possession boundary, so the following
        // drive's plays flip possession and must be fully reintegrated
        // (fragment player refs swapped + pending drives rebuilt) rather than
        // routed through the light future-context path.
        // Kickoffs and coin tosses are possession-establishing special-teams
        // plays that validation explicitly permits with a null drive, so their
        // deletion is intentionally left to the light future path. Only a
        // scrimmage change-of-possession (interception, fumble lost, downs,
        // punt) dissolves a drive boundary and needs the following drive merged.
        const isSpecialTeamsPossession =
            playToDelete.historyContains(FootballState.KickOff) ||
            playToDelete.historyContains(FootballState.CoinToss);
        const nextPlay = await this._getNextPlay(playToDelete);
        const isTurnover =
            !isSpecialTeamsPossession &&
            nextPlay &&
            nextPlay.isComplete() &&
            nextPlay.contextSnapshot.homeTeamHasPossession !==
                playToDelete.contextSnapshot.homeTeamHasPossession;

        // For a turnover the following drive is pulled into the reintegration
        // range below, where the full path regenerates each play's snapshot and
        // relies on the *original* snapshot to detect the possession flip and
        // swap player refs. Carrying the snapshot over would clobber that
        // original and defeat the swap, so only carry it for non-turnover
        // deletions (where the next play stays on the light future path).
        if (!isTurnover) {
            await this._carryOverContextSnapshot(playToDelete);
        }
        await this._deletePlayAndUpdateDrive(id);
        const playIndexes = await this._getReintegrationRange(playToDelete);

        if (isTurnover) {
            const followingDriveRange = await this._getReintegrationRange(
                nextPlay
            );
            playIndexes.push(...followingDriveRange);
        }

        return this.reintegratePlays(
            playToDelete.gameId,
            Math.min(...playIndexes),
            Math.max(...playIndexes)
        );
    }

    async deletePlayAndUpdateContext(id) {
        const isLastPlay = await this.isLastCompletePlay(id);
        return isLastPlay
            ? this._deleteLastPlay(id)
            : this._deleteHistoricalPlay(id);
    }

    async getDrivePlays(gameId, driveId) {
        if (!driveId) {
            return [];
        }
        const game = await GameService.getGame(gameId);
        return (game?.plays || []).filter(
            (p) => p.driveId === driveId && p.isComplete()
        );
    }

    /**
     * Determine if the play corresponding to the provided playId, is deletable based on the provided plays.
     * @param {string} playId
     * @param {FootballPlay[]} plays
     * @param {Drive} activeDrive
     * @returns {boolean}
     * @private
     */
    _isDeletable(play, plays, activeDrive, lastCompletePlay) {
        const nextPlay = plays.find((p) => p.index === play.index + 1);

        const allowedStates = [
            FootballState.Rush,
            FootballState.Pass,
            FootballState.Punt,
            FootballState.FieldGoal,
            FootballState.KneelDown,
            FootballState.PlayPenalty,
            FootballState.Spike,
            FootballState.Timeout,
            FootballState.UpdateContext,
            FootballState.PatTry,
            FootballState.KickOff,
            FootballState.CoinToss
        ];

        const isTurnover =
            nextPlay &&
            nextPlay.contextSnapshot.homeTeamHasPossession !==
                play.contextSnapshot.homeTeamHasPossession;

        const isCurrentDrive = play.driveId && play.driveId === activeDrive?.id;

        const isTimeOnlyPlay =
            play.fragments?.find(
                (f) => f.associatedState === FootballState.SetTimeAndPeriod
            )?.timeAndPeriod?.period === play.contextSnapshot.currentPeriod;

        return (
            lastCompletePlay?.id === play.id ||
            isTimeOnlyPlay ||
            (isCurrentDrive && isTurnover) ||
            (allowedStates.some((s) => play.historyContains(s)) && !isTurnover)
        );
    }

    /**
     * Recompute drive end-clock data and time-of-possession for every drive
     * in the given game by reintegrating all complete plays. Pushes a fresh
     * boxscore after the rebuild. Useful for in-progress games whose drive
     * `totalTime`/`endTime` values were corrupted by earlier bugs.
     * @param {string} gameId
     * @returns {Promise<{FootballGameContext}|null>} Resulting game context, or null if no complete plays.
     */
    async rebuildDriveTimes(gameId) {
        // Drop any stray incomplete plays before rebuilding. Reintegration only
        // ever covers complete plays, so an orphan ADD_PLAY left behind by an
        // earlier corruption survives the rebuild and then collides with a
        // complete play once the complete plays are re-indexed — and because
        // getOrCreatePlay binds to the first incomplete play it finds, the next
        // entered play would land on that stray (stale context, wrong index).
        // A real in-progress play is recreated on demand by getOrCreatePlay, so
        // discarding incomplete plays here is safe.
        await this._purgeIncompletePlays(gameId);

        const plays = (await this.getGamePlays(gameId)).filter((p) =>
            p.isComplete()
        );
        if (!plays.length) {
            return null;
        }
        const indexes = plays.map((p) => p.index);
        const result = await this.reintegratePlays(
            gameId,
            Math.min(...indexes),
            Math.max(...indexes)
        );

        // Persist gap-closed drive times for every drive. Reintegration
        // skips the last drive (treated as in-progress), so its end clock
        // may still be stale. computeDriveTotalTimes uses next-drive starts
        // (or current game clock for the last drive) to produce values that
        // sum to game elapsed time.
        const gameContext = await ContextService.getGameContext(gameId);
        const drives = await DriveService.getByGameId(gameId);
        DriveService.computeDriveTotalTimes(drives, gameContext);
        for (const drive of drives) {
            const fresh = await DriveService.getById(drive.id);
            if (fresh) {
                fresh.endTime = drive.endTime;
                fresh.endPeriod = drive.endPeriod;
                fresh.totalTime = drive.totalTime;
                await DriveService.saveDrive(fresh);
            }
        }

        await FootballBoxscoreService.saveBoxscoreByGameId(gameId);
        return result;
    }

    /**
     * Delete every incomplete (not finalized) play for a game. Used by the
     * drive-time rebuild to clear orphan ADD_PLAY plays that earlier corruption
     * can leave mid-game; the live pending play is recreated on demand by
     * getOrCreatePlay.
     * @param {string} gameId
     * @private
     */
    async _purgeIncompletePlays(gameId) {
        const incomplete = (
            await playRepository.getPlaysWithFragments(gameId)
        ).filter((p) => !p.isComplete());

        for (const play of incomplete) {
            // Delete raw — NOT via deletePlay, which decrements every following
            // play's index. A stray often shares an index with a real play, so
            // shifting indexes here would corrupt the surviving complete plays;
            // the gap (if any) is harmless and reintegration re-derives order.
            await fragmentRepository.where({ playId: play.id }).delete();
            await statsRepository.where({ playId: play.id }).delete();
            await playRepository.delete(play.id);
        }
    }

    /**
     * Guarantee play indexes are contiguous (1..N, no gaps or duplicates).
     * Reintegration can leave gaps (a stray play deleted raw) or duplicates
     * (a pre-existing orphan colliding with a re-homed play). Order every play
     * by current index, breaking ties by creation order, and reassign a dense
     * 1-based sequence. Only the plays whose index actually changes are
     * written. Drive associations (`driveId`) are untouched.
     * @param {string} gameId
     * @private
     */
    async _resequencePlayIndexes(gameId) {
        const plays = (
            await playRepository.where({ gameId: gameId }).toArray()
        ).sort((a, b) => {
            if (a.index !== b.index) {
                return a.index - b.index;
            }
            return (
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            );
        });

        const renumbered = plays
            .map((play, position) => ({ play, index: position + 1 }))
            .filter(({ play, index }) => play.index !== index)
            .map(({ play, index }) => Object.assign(play, { index }));

        if (renumbered.length) {
            await playRepository.bulkPut(renumbered);
        }
    }

    /**
     * Build the boxscore JSON payload that would be posted to the Gameday
     * API /data endpoint, without actually posting it. Useful for support
     * tooling (download / inspect / diff).
     * @param {string} gameId
     * @returns {Promise<object>} The serialized boxscore object.
     */
    async getBoxscoreJson(gameId) {
        const { eventId } = await GameService.getGame(gameId);
        return FootballBoxscoreService.serialize(eventId);
    }

    /**
     * Reintegrate (reprocess) plays for a given Game, update the Game Context, based on the provided startIndex and endIndex.
     * @param {string} gameId
     * @param {number} startIndex
     * @param {number} endIndex
     * @returns {Promise<{FootballGameContext}>} Game Context resulting from the play reintegration.
     */
    async reintegratePlays(gameId, startIndex, endIndex) {
        const game = await GameService.getGame(gameId);
        const { awayTeam, homeTeam } = await this.getGameTeams(gameId);
        const plays = (game?.plays || []).filter((p) => p.isComplete());
        const rangePlays = plays.filter(
            (p) => p.index >= startIndex && p.index <= endIndex
        );
        const futurePlays = plays.filter((p) => p.index > endIndex);
        const { id: startPlayId, driveId } = plays.find(
            (p) => p.index === startIndex
        );

        await this._prepFutureDrives(gameId, driveId, rangePlays);
        await this._updateContextToPreviousSnapshot(startPlayId);
        await this._clearReintegrationData(gameId, rangePlays);
        const { gameContext: resultingRangeContext } =
            await this._reintegratePlaysAndFragments(
                gameId,
                rangePlays,
                awayTeam,
                homeTeam
            );

        await this._reintegrateDrives(gameId);

        const { gameContext: lastReintegratedContext } =
            await this._reintegrateContexts(
                gameId,
                futurePlays,
                resultingRangeContext,
                homeTeam,
                awayTeam
            );

        await this._pruneOrphanedDrives(gameId);
        await this._resequencePlayIndexes(gameId);

        return FootballSerializer.serializeGameContext(
            await ContextService.updateContext(lastReintegratedContext)
        );
    }

    /**
     * Remove drives orphaned by reintegration. `_clearReintegrationData` only
     * deletes *incomplete* kickoff drives, so when a reintegration range covers
     * a previously-completed drive's kickoff that drive is left in place while
     * reintegration creates a fresh drive for the same plays — leaving the old
     * one behind with no plays. In the implicit-drive model a drive exists only
     * while it has at least one play, with one exception: the eagerly-opened
     * current drive (always the highest-index drive) may legitimately hold no
     * plays yet. So any *interior* (non-last) drive with zero plays is an
     * orphan. Indexes are closed up so the surviving drives stay contiguous.
     * @param {string} gameId
     * @private
     */
    async _pruneOrphanedDrives(gameId) {
        const lastDrive = await DriveService.getLastDrive(gameId);
        if (!lastDrive) {
            return;
        }
        const orphans = (await DriveService.getByGameId(gameId))
            .filter((d) => d.index < lastDrive.index && !(d.plays || []).length)
            .sort((a, b) => b.index - a.index);

        for (const drive of orphans) {
            await this._propagateDriveIndexes(gameId, drive.index, {
                decrement: true,
                future: true
            });
            await DriveService.deleteDrive(gameId, drive.id);
        }
    }

    /**
     * End the specified drive using the provided gameContext.
     * @param {Drive} drive
     * @param {FootballGameContext} gameContext
     * @returns {Promise<void>}
     * @private
     */
    async _manuallyEndDrive(drive, gameContext) {
        const { minutesByPeriod } = gameContext.gameConfig.general;
        await DriveService.setEndTimeToDrive(
            drive,
            gameContext.getTime(),
            gameContext.getCurrentPeriod(),
            minutesByPeriod
        );
        await DriveService.endDrive(gameContext);
    }

    /**
     * Clean up by ending any incomplete drives that are not the current drive.
     * @param {string} gameId
     * @returns {Promise<void>}
     * @private
     */
    async _reintegrateDrives(gameId) {
        const lastDrive = await DriveService.getLastDrive(gameId);
        const incompleteDrives = (
            await DriveService.getIncompleteDrives(gameId)
        ).filter((d) => d.id !== lastDrive.id);

        for (const drive of incompleteDrives) {
            const lastDrivePlay = (
                await this.getDrivePlays(drive.gameId, drive.id)
            ).pop();
            // In the implicit-drives model a drive can exist with no plays yet
            // assigned (created by _startDriveWithoutPlay but not yet played).
            // Skip closing such drives — they are still in progress.
            if (!lastDrivePlay) {
                continue;
            }
            const { gameContext: driveResultContext } =
                await this._reprocessPlay(lastDrivePlay.id);
            await this._manuallyEndDrive(drive, driveResultContext);
        }
    }

    /**
     * Check the provided plays for new Drive Start plays and increment future drive's indexes accordingly.
     * @param gameId
     * @param driveId
     * @param plays
     * @returns {Promise<void>}
     * @private
     */
    async _prepFutureDrives(gameId, driveId, plays) {
        const newDriveCount = plays.filter(
            (p) => !p.driveId && p.historyContains(FootballState.StartDrive)
        ).length;
        if (newDriveCount && driveId) {
            const { index: driveIndex } = await DriveService.getById(driveId);
            await this._propagateDriveIndexes(gameId, driveIndex, {
                future: true,
                by: newDriveCount
            });
        }
    }

    /**
     * Delete all drive, stat, fragment, and play data from the provided gameId and plays.
     * @param {string} gameId
     * @param {FootballPlay[]} plays
     * @returns {Promise<void>}
     * @private
     */
    async _clearReintegrationData(gameId, plays = []) {
        const deleteDriveIds = plays
            .filter(
                (fp) =>
                    fp.historyContains(FootballState.StartDrive) && fp.driveId
            )
            .map((fp) => fp.driveId);

        // In the implicit-drives model drives are created by _startDriveWithoutPlay
        // (triggered after a KickOff). If the reintegration range contains the KO
        // play that started an open (not-yet-completed) drive, delete that drive so
        // reintegration can recreate it with correct indices and associations.
        const koPlaysInRange = plays.filter(
            (fp) => fp.historyContains(FootballState.KickOff) && fp.driveId
        );
        for (const koPlay of koPlaysInRange) {
            const drive = await DriveService.getById(koPlay.driveId);
            if (
                drive &&
                !drive.completed &&
                !deleteDriveIds.includes(koPlay.driveId)
            ) {
                deleteDriveIds.push(koPlay.driveId);
            }
        }
        const deleteStats = await statsRepository
            .where("playId")
            .anyOf(plays.map((fp) => fp.id))
            .toArray();
        const deleteFragments = await fragmentRepository
            .where("playId")
            .anyOf(plays.map((fp) => fp.id))
            .toArray();
        await statsRepository.bulkDelete(deleteStats.map((s) => s.id));
        await playRepository.bulkDelete(plays.map((fp) => fp.id));
        await fragmentRepository.bulkDelete(deleteFragments.map((df) => df.id));
        await Promise.all(
            deleteDriveIds.map((driveId) =>
                DriveService.deleteDrive(gameId, driveId)
            )
        );
    }

    /**
     * Reintegrate by retrieving an updated context snapshot and fragment data for each play before saving and processing.
     * @param {string} gameId
     * @param {FootballPlay[]} plays
     * @param {Team} awayTeam
     * @param {Team} homeTeam
     * @returns {Promise<FootballGameContext>} Game Context of the last reintegrated play.
     * @private
     */
    async _reintegratePlaysAndFragments(gameId, plays, awayTeam, homeTeam) {
        const awayNaPlayer = (await PlayerService.getPlayers(awayTeam.id)).find(
            (p) => p.uniform === NONE_UNIFORM
        );
        const homeNaPlayer = (await PlayerService.getPlayers(homeTeam.id)).find(
            (p) => p.uniform === NONE_UNIFORM
        );
        const { eventId } = await gameRepository.getById(gameId);
        return plays.reduce(async (acc, play) => {
            const retAcc = await acc;

            const contextSnapshot = await this._getReintegratedContextSnapshot(
                gameId,
                awayTeam,
                homeTeam
            );

            const updatedFragments = this._getReintegratedFragments(
                play,
                contextSnapshot,
                homeTeam,
                awayTeam,
                homeNaPlayer,
                awayNaPlayer
            );

            const previousPlayId = play.id;
            const savePlay = await this.savePlay(
                Object.assign(play, {
                    id: undefined,
                    contextSnapshot,
                    driveId: null,
                    fragments: updatedFragments
                })
            );
            await StreamService.updateMarkerPlayIdRefs(
                eventId,
                previousPlayId,
                savePlay.id
            );
            await this._processPlay(savePlay.id);
            // Mirror the normal endTransition flow: after processing a play,
            // handle any pending drive/PAT/KO triggers (e.g. KO → startDrive).
            await this._createPendingPlays(gameId);
            // Carry the live context (including eager drive state set by
            // _createPendingPlays) into future-play reintegration instead of
            // reverting to the play's pre-pending snapshot.
            retAcc.gameContext = await ContextService.getGameContext(gameId);

            return retAcc;
        }, Promise.resolve({ gameContext: null }));
    }

    /**
     * Update and save the context snapshot of each provided play based on a startingContext.
     * @param {string} gameId
     * @param {FootballPlay[]} plays
     * @param {FootballGameContext} startingContext
     * @param {Team} homeTeam
     * @param {Team} awayTeam
     * @returns {Promise<FootballGameContext>} The resulting Game Context after reprocessing the last provided play.
     * @private
     */
    async _reintegrateContexts(
        gameId,
        plays,
        startingContext,
        homeTeam,
        awayTeam
    ) {
        return plays.reduce(
            async (acc, play, index) => {
                const retAcc = await acc;
                const { gameContext } = retAcc;

                const contextSnapshot =
                    await this._getReintegratedContextSnapshot(
                        gameId,
                        awayTeam,
                        homeTeam,
                        gameContext
                    );

                const isFirstFuturePlay = index === 0;
                if (isFirstFuturePlay) {
                    play.contextSnapshot = contextSnapshot;
                } else {
                    play.contextSnapshot.score = contextSnapshot.score;
                }

                await this.savePlay(play);

                const { statLogs, gameContext: reprocessedContext } =
                    await this._reprocessPlay(play.id);
                retAcc.gameContext = reprocessedContext;

                await FootballStatsService.deleteByPlayId(play.id);
                await FootballStatsService.savePlayStatLogs(play.id, statLogs);

                return retAcc;
            },
            Promise.resolve({
                gameContext: startingContext
            })
        );
    }

    /**
     * Generate a valid context snapshot so that it can be used for play processing.
     * @param {string} gameId
     * @param {Team} awayTeam
     * @param {Team} homeTeam
     * @param {Optional<FootballGameContext>} gameContext
     * @returns {Promise<FootballGameContextSnapshot>}
     * @private
     */
    async _getReintegratedContextSnapshot(
        gameId,
        awayTeam,
        homeTeam,
        gameContext = null
    ) {
        const context =
            gameContext || (await ContextService.getGameContext(gameId));

        return context.generateSnapshot();
    }

    /**
     * Use the provided contextSnapshot to determine if possession has changed for a given play, update fragments of the type Player and Players where necessary.
     * @param {FootballPlay} play
     * @param {FootballGameContextSnapshot} contextSnapshot
     * @param {Team} homeTeam
     * @param {Team} awayTeam
     * @param {TeamPlayer} homeNaPlayer
     * @param {TeamPlayer} awayNaPlayer
     * @returns {Promise<Fragment[]>} Updated Fragments to be used for play processing.
     * @private
     */
    _getReintegratedFragments(
        play,
        contextSnapshot,
        homeTeam,
        awayTeam,
        homeNaPlayer,
        awayNaPlayer
    ) {
        return play.fragments.reduce((acc, fragment) => {
            const possessionChanged =
                play.contextSnapshot.homeTeamHasPossession !==
                contextSnapshot.homeTeamHasPossession;

            fragment.requiredData.forEach((rd) => {
                const rdValue = fragment[rd.name];
                switch (rd.type) {
                    case FragmentDataType.Player:
                        if (possessionChanged && rdValue) {
                            fragment[rd.name] =
                                this._getReintegratedFragmentPlayer(
                                    rdValue,
                                    homeTeam,
                                    awayTeam,
                                    homeNaPlayer,
                                    awayNaPlayer
                                );
                        }
                        break;
                    case FragmentDataType.Players:
                        if (possessionChanged && rdValue.length) {
                            fragment[rd.name] = [
                                this._getReintegratedFragmentPlayer(
                                    rdValue[0],
                                    homeTeam,
                                    awayTeam,
                                    homeNaPlayer,
                                    awayNaPlayer
                                )
                            ];
                        }
                        break;
                    default:
                        break;
                }
            });
            acc.push(fragment);
            return acc;
        }, []);
    }

    /**
     * Retrieve fragment player data that uses the opposing team's NA Player.
     * @param {{teamId: string, id: string, playerId: string | null}} rdValue
     * @param {Team} homeTeam
     * @param {Team} awayTeam
     * @param {TeamPlayer} homeNaPlayer
     * @param {TeamPlayer} awayNaPlayer
     * @returns {{teamId: string, id: string, playerId: null}}
     * @private
     */
    _getReintegratedFragmentPlayer(
        rdValue,
        homeTeam,
        awayTeam,
        homeNaPlayer,
        awayNaPlayer
    ) {
        const updatedTeamId =
            rdValue.teamId === homeTeam.id ? awayTeam.id : homeTeam.id;
        const updatedPlayerId = (
            updatedTeamId === homeTeam.id ? homeNaPlayer : awayNaPlayer
        ).id;

        return Object.assign(rdValue, {
            teamId: updatedTeamId,
            playerId: null,
            id: updatedPlayerId
        });
    }

    /**
     * Update future or past drive indexes based on the provided options
     * @param gameId
     * @param startIndex
     * @param {Partial<{decrement: boolean, future: boolean, by: number}>} options
     * @returns {Promise<void>}
     * @private
     */
    async _propagateDriveIndexes(gameId, startIndex, options = {}) {
        const { decrement, future, by } = {
            decrement: false,
            future: false,
            by: 1,
            ...options
        };
        const updatedDrives = (
            await driveRepository
                .where({
                    gameId: gameId
                })
                .toArray()
        )
            .filter((d) =>
                future ? d.index > startIndex : d.index < startIndex
            )
            .map((d) =>
                Object.assign(d, {
                    index: decrement ? d.index - by : d.index + by
                })
            );

        await driveRepository.bulkPut(updatedDrives);
    }

    /**
     * Update future or past play indexes based on the provided options
     * @param {FootballPlay} play
     * @param {Partial<{decrement: boolean, future: boolean}>} options
     * @returns {Promise<void>}
     */
    async propagatePlayIndexes(
        play,
        options = { decrement: false, future: false }
    ) {
        const { decrement, future } = options;
        const updatedPlays = (
            await playRepository
                .where({
                    gameId: play.gameId
                })
                .toArray()
        )
            .filter(
                (p) =>
                    p.id !== play.id &&
                    (future ? p.index >= play.index : p.index <= play.index)
            )
            .map((p) =>
                Object.assign(p, {
                    index: decrement ? p.index - 1 : p.index + 1
                })
            );

        await playRepository.bulkPut(updatedPlays);
    }

    async deletePlay(id) {
        try {
            await transactionManager.transaction(
                "rw",
                [playRepository, fragmentRepository, statsRepository],
                async () => {
                    const play = await playRepository.getById(id);
                    if (play) {
                        fragmentRepository.where({ playId: id }).delete();
                        statsRepository.where({ playId: id }).delete();
                        playRepository.delete(id);
                        await this.propagatePlayIndexes(play, {
                            decrement: true,
                            future: true
                        });
                    }
                }
            );
        } catch (e) {
            console.error(e);
        }
    }

    async baseAddPlay(gameId, play) {
        const game = await gameRepository.getById(gameId);
        play.gameId = game.id;
        play.addPlay();

        const gameContext = await ContextService.getGameContext(game.id);
        const contextSnapshot = gameContext.generateSnapshot();
        play.setContextSnapshot(contextSnapshot);
        play.time = contextSnapshot.getTime();

        return this.savePlay(play);
    }

    async addPlay(gameId) {
        const play = new FootballPlay();
        return this.baseAddPlay(gameId, play);
    }

    async _processPlay(playId) {
        let play = await this.getPlay(playId);
        const game = await gameRepository.getById(play.gameId);
        let [gameContext, eventConfiguration, event] = await Promise.all([
            ContextService.getGameContext(game.id),
            this.getEventConfigurationByGameId(game.id),
            EventService.getById(game.eventId)
        ]);

        const statLogs = await this.ruleSetProcessor.process(
            gameContext,
            eventConfiguration,
            { play },
            event
        );

        await Promise.all([
            ContextService.updateContext(gameContext),
            FootballStatsService.savePlayStatLogs(playId, statLogs)
        ]);

        const { time, period } = await this.getCurrentTimeAndPeriod(
            play.gameId
        );
        Object.assign(play, { time, period });

        return this.savePlay(play);
    }

    async _modifiesTransitionSequence(play, transition, fromStateIndex) {
        if (fromStateIndex) {
            let nextState = play.getStateAfter(fromStateIndex);
            if (nextState) {
                return nextState.name !== transition.to;
            } else {
                const pendingFragment = play.getPendingFragment();
                if (pendingFragment) {
                    return pendingFragment.associatedState !== transition.to;
                }
            }
        }

        return false;
    }

    /**
     * Revert a play to a previous transition by using the provided associatedHistoryIndex
     * @param playId
     * @param associatedHistoryIndex
     * @returns {Promise<{play: *, createdPlays: *, gameContext: *, wizardData: *}|{play: null, createdPlays: *, gameContext: *, wizardData: null}>}
     */
    async revertToTransition(playId, associatedHistoryIndex) {
        let play = await this.getPlay(playId);

        if (associatedHistoryIndex) {
            play = await this.goBackTo(play.id, associatedHistoryIndex);
        }

        return this._getPlayResult(play);
    }

    async startTransition(playId, transitionName, fromStateIndex) {
        const play = await this._startTransition(
            playId,
            transitionName,
            fromStateIndex
        );

        return this._getPlayResult(play);
    }

    async _startTransition(playId, transitionName, fromStateIndex) {
        let play = await this.getPlay(playId);
        const transition = play.getTransition(transitionName);
        const nextStateName = transition.to;

        const nextStateIndex = fromStateIndex
            ? fromStateIndex + 1
            : play.getHistory().length;

        const modifiesTransition = await this._modifiesTransitionSequence(
            play,
            transition,
            fromStateIndex
        );

        if (modifiesTransition) {
            play = await this.goBackTo(play.id, fromStateIndex);
        }

        const fragment = await this._getOrCreateFragment(play, {
            associatedState: nextStateName,
            associatedTransition: transitionName,
            associatedHistoryIndex: nextStateIndex
        });

        // If no fragment is required for the next step it means we don't need to collect any data.
        // So we could end the transition.
        if (!fragment || play.canEndTransition(fragment)) {
            await this.endTransition(play.id, transitionName);
        }

        return play;
    }

    async _getPlayResult(currentPlay) {
        let createdPlays = await this.getPlaysAfterIncluding(currentPlay);
        const activeDrive = await DriveService.getCurrentDrive(
            currentPlay.gameId
        );
        let gameContext = await ContextService.getGameContext(
            currentPlay.gameId
        );

        createdPlays = this._withFlags(
            await this._withMessages(createdPlays, currentPlay.gameId),
            activeDrive,
            gameContext
        );

        const lastPlay = [...createdPlays]
            .reverse()
            .find((p) => !p.isComplete());

        if (!lastPlay && gameContext.clockType === "running") {
            gameContext = await ContextService.resumeGameClock(
                currentPlay.gameId,
                { onlyIfByPlaySequence: true }
            );
        }

        gameContext = FootballSerializer.serializeGameContext(gameContext);

        if (lastPlay) {
            const wizardData = await this.getWizardData(lastPlay);

            return {
                play: lastPlay,
                gameContext,
                wizardData,
                createdPlays
            };
        } else {
            return {
                play: null,
                gameContext,
                wizardData: null,
                createdPlays
            };
        }
    }

    async _finalizeDrive(gameContext) {
        await DriveService.endDrive(gameContext);
        // Fire-and-forget: the boxscore push hits the Gameday API and must not
        // block live play entry. Attach a catch so a rejected push stays a
        // handled (logged) failure instead of an unhandled rejection.
        FootballBoxscoreService.saveBoxscoreByGameId(gameContext.gameId).catch(
            () => {}
        );

        gameContext.currentDriveId = null;
        return ContextService.updateContext(gameContext);
    }

    async _createPendingPlays(gameId) {
        let gameContext = await ContextService.getGameContext(gameId);
        const game = await GameService.getGame(gameId);
        const { triggerPAT, triggerKO, currentDriveId } = gameContext;
        const { triggerDrive } = gameContext.getDriveContext();
        if (triggerPAT) {
            await this._finalizeDrive(gameContext);
        } else if (triggerDrive) {
            await this._finalizeDrive(gameContext);
            await this._startDriveWithoutPlay(game.id);
        } else if (triggerKO && currentDriveId) {
            // Standalone OT PATs don't set triggerPAT, so the drive
            // isn't finalized above. End it before the kickoff unless
            // the drive was already handled (e.g. safety/FG flows).
            const driveEndType =
                gameContext.getDriveContext()?.previousDriveEndType?.code;
            if (!KICKOFF_DRIVE_END_TYPES.includes(driveEndType)) {
                await this._finalizeDrive(gameContext);
            }
        }
    }

    async getSingleTransitionAvailable(play) {
        const gameContext = await ContextService.getGameContext(play.gameId);
        const coinTossRequired = await this._getCoinTossRequired(
            play,
            gameContext
        );
        const allowedTransitions = this._getApplicableTransitions(
            gameContext,
            play,
            play.getAllowedTransitions(),
            play.getLastFragment(),
            coinTossRequired
        );
        if (allowedTransitions.length === 1) {
            const transitionName = allowedTransitions.pop().name;
            if (transitionName !== FootballTransition.Finalize) {
                return transitionName;
            }
        }

        return null;
    }

    async _forcePeriodChange(play) {
        // We need to remove the current pending play because
        // it's associated to previous period and we need to insert Start Period play first
        const existingFragments = await this.getFragments(play.id);
        await this.deletePlay(play.id);
        const { createdPlays } = await this.setPeriod(
            play.gameId,
            play.getContextSnapshot().currentPeriod + 1
        );
        const gameContext = await ContextService.getGameContext(play.gameId);

        const isMissingDrivePlay =
            play.historyContains(FootballState.StartDrive) &&
            !createdPlays.some((play) =>
                play.historyContains(FootballState.StartDrive)
            );

        if (isMissingDrivePlay) {
            // First drive of second half and first drive of 1OT is created into prev function
            await this._createFirstDriveOfPeriod(play.gameId);
        }

        const endOfPlayFragment =
            play.getFirstFragment(FootballState.EndOfPlay) ||
            play.getFirstFragment(FootballState.NoPenaltyEndOfPlay);
        const isEndOfPlayCollection = endOfPlayFragment?.getTimeAndPeriod();

        if (isEndOfPlayCollection) {
            // Restore deleted play, and ensure that it comes after the Start Period play
            const { index: periodPlayIndex, period: periodPlayPeriod } =
                createdPlays.pop() || {};
            await this.savePlay(
                Object.assign(play, {
                    index: periodPlayIndex + 1,
                    fragments: existingFragments,
                    period: periodPlayPeriod,
                    contextSnapshot: gameContext.generateSnapshot()
                })
            );
            await this._processPlay(play.id);
            await this._createPendingPlays(play.gameId);
        }
    }

    async _changePeriodRequired(play) {
        if (play.isComplete()) {
            const gameContext = await ContextService.getGameContext(
                play.gameId
            );
            const currentPeriod = gameContext.getCurrentPeriod();
            const driveFragment = play.getFirstFragment(
                FootballState.StartDrive
            );
            if (
                driveFragment &&
                driveFragment.startTime.period !== currentPeriod
            ) {
                return true;
            }

            const endOfPlay =
                play.getFirstFragment(FootballState.EndOfPlay) ||
                play.getFirstFragment(FootballState.NoPenaltyEndOfPlay);
            const { period } = endOfPlay?.getTimeAndPeriod() || {};
            if (period && period !== currentPeriod) {
                return true;
            }
        }

        return false;
    }

    // Check if only one transition is available and attempt to enter it if all requiredAfterNextState requiredData is satisfied.
    // Reasoning for the nextStateReqStatisfied & hasRequiredPartialData check, is so we do not automatically transition while awaiting fragment data.
    // It is possible that once requiredData using requiredAfterNextState is entered, additional transitions may appear (eg. Safety)
    async _attemptSingleTransition(play) {
        const nextTransition = await this.getSingleTransitionAvailable(play);
        const lastFragment = play.getLastFragment();
        const shouldTransition =
            nextTransition &&
            play.nextStateReqSatisfied(lastFragment.associatedState) &&
            lastFragment.hasRequiredPartialData();

        if (shouldTransition) {
            const gameContext = await ContextService.getGameContext(
                play.gameId
            );
            if (!play.requiresDataForTransition(nextTransition, gameContext)) {
                await this.endTransition(play.id, nextTransition);
            } else {
                await this.startTransition(play.id, nextTransition);
            }
        }
    }

    async endTransition(playId, transitionName, fragmentData) {
        let play = await this.getPlay(playId);
        if (!play.canTransitionTo(transitionName)) {
            throw new Error(`Invalid Transition: ${transitionName}`);
        }
        const gameContext = await ContextService.getGameContext(play.gameId);

        play[transitionName](fragmentData, gameContext);

        await ValidationService.validateFootball(play);

        const isChangePeriod = await this._changePeriodRequired(play);
        if (isChangePeriod) {
            await this._forcePeriodChange(play);
        } else {
            play = await this.savePlay(play);
            if (play.isComplete()) {
                play = await this._processPlay(playId);
            }

            await this._attemptSingleTransition(play);

            if (play.isComplete()) {
                await this._createPendingPlays(play.gameId);
            }
        }
    }

    async getPlaysAfterIncluding(play) {
        const plays = await playRepository.getPlaysWithFragments(play.gameId);

        return plays
            .filter((p) => p.index >= play.index)
            .sort((a, b) => a.index - b.index);
    }

    async getWizardData(play) {
        const { gameId } = play;
        const [{ homeTeam, awayTeam }, gameContext, eventConfiguration] =
            await Promise.all([
                this.getGameTeams(gameId),
                ContextService.getGameContext(gameId),
                this.getEventConfigurationByGameId(gameId)
            ]);

        const validationContext = ValidationService.validatePlay(
            play,
            gameContext,
            eventConfiguration
        );

        const coinTossRequired = await this._getCoinTossRequired(
            play,
            gameContext
        );

        const steps = WizardService.getSteps(
            play,
            homeTeam,
            awayTeam,
            gameContext,
            validationContext,
            coinTossRequired,
            this
        );
        const activeStep = WizardService.getActiveStepIndex(play, steps);

        return { steps, activeStep };
    }

    async goBack(playId) {
        const play = await playRepository.getById(playId);
        await play.goBack();
        playRepository.put(play);
        return play;
    }

    async goForward(playId) {
        const play = await playRepository.getById(playId);
        await play.goForward();
        playRepository.put(play);
        return play;
    }

    async refreshPlayerCache(gameId) {
        if (gameId) {
            const { homeTeam, awayTeam } = await this.getGameTeams(gameId);
            if (homeTeam) {
                await PlayerService.getPlayers(homeTeam.id);
            }
            if (awayTeam) {
                await PlayerService.getPlayers(awayTeam.id);
            }
        }
    }

    /**
     * Determine if the play corresponding to the provided playId, can have a play inserted.
     * @param {FootballPlay} play
     * @param {FootballPlay[]} plays
     * @param {PlayInsertionType} insertType
     * @returns {boolean}
     */
    _isInsertable(play, plays, insertType) {
        if (play && play.isComplete()) {
            const filteredPlays = plays.filter((p) => p.isComplete());
            const nextPlay = filteredPlays.find(
                (p) => p.index === play.index + 1
            );

            if (insertType === PlayInsertionType.Before) {
                return true;
            }
            if (insertType === PlayInsertionType.After) {
                return !!nextPlay;
            }
            if (insertType === PlayInsertionType.Replace) {
                const restrictedStates = [FootballState.StartPeriod];
                return !restrictedStates.some((s) => play.historyContains(s));
            }
        }

        return false;
    }

    _canEditKickReceiving(play, lastCompletePlay, gameContext) {
        if (play.id === lastCompletePlay?.id) {
            const startPeriodFragment = play.fragments.find(
                (f) => f.associatedState === FootballState.StartPeriod
            );

            if (startPeriodFragment) {
                const { period } = startPeriodFragment;

                const isStartOfSecondHalf =
                    period === secondHalfPeriodNumber(gameContext);

                const isOT = isOvertimePeriod(
                    period,
                    gameContext.gameConfig.general.periodsInRegulation
                );

                return isStartOfSecondHalf || isOT;
            }
        }

        return false;
    }

    /**
     * Assign flags to an array of plays.
     * @param {FootballPlay[]} plays
     * @param {Drive} activeDrive
     * @param {GameContext} gameContext
     * @returns {FootballPlay[]}
     * @private
     */
    _withFlags(plays, activeDrive, gameContext, drives = []) {
        const lastCompletePlay = plays
            .filter((p) => p.isComplete())
            .sort((p1, p2) => p2.index - p1.index)[0];

        const driveById = new Map((drives || []).map((d) => [d.id, d]));

        // First/last play index per drive, used to badge drive boundaries in
        // the play list now that there is no synthetic Start of Drive play.
        // Single pass per drive: `min`/`max` bound the drive for the drive-end
        // badge, while `startIndex` is the first NON-PAT play. A drive never
        // *begins* with a PAT (a try is an appendage to a scoring play), so a
        // PAT mis-linked to a drive (e.g. a defensive-TD PAT pulled into a later
        // drive) cannot steal the START badge from the drive's real first play.
        const driveBounds = new Map();
        plays
            .filter((p) => p.driveId && p.isComplete())
            .forEach((p) => {
                const bounds = driveBounds.get(p.driveId) || {
                    min: p.index,
                    max: p.index,
                    startIndex: undefined
                };
                bounds.min = Math.min(bounds.min, p.index);
                bounds.max = Math.max(bounds.max, p.index);
                if (
                    !p.historyContains(FootballState.PatTry) &&
                    (bounds.startIndex === undefined ||
                        p.index < bounds.startIndex)
                ) {
                    bounds.startIndex = p.index;
                }
                driveBounds.set(p.driveId, bounds);
            });

        return plays.map((play) => {
            play.isDeletable = this._isDeletable(
                play,
                plays,
                activeDrive,
                lastCompletePlay
            );
            // Whether the play carries an operator-collected clock (vs an
            // inherited one). Authoritative times bound time edits in the UI.
            play.timeCollected = play.hasCollectedTime();
            const bounds = play.driveId ? driveBounds.get(play.driveId) : null;
            play.isDriveStart =
                !!bounds &&
                bounds.startIndex !== undefined &&
                play.index === bounds.startIndex;
            // Drive end is only marked once the drive is complete (i.e. it is
            // not the current in-progress drive).
            play.isDriveEnd =
                !!bounds &&
                play.index === bounds.max &&
                play.driveId !== activeDrive?.id;
            // Surface the drive's end clock on its last play so the UI can show
            // and edit it. A drive whose end clock equals its start captured no
            // elapsed time (it ended on a turnover/downs/fumble play that
            // collects none), so flag it for the operator to supply the end
            // time (defaulting to the current clock). Scoring/kickoff drives
            // advance the clock, so they are not flagged.
            if (play.isDriveEnd) {
                const drive = driveById.get(play.driveId);
                play.driveEndTime = drive ? drive.endTime : null;
                play.driveEndPeriod = drive ? drive.endPeriod : null;
                // The authoritative drive time-of-possession (same value the
                // boxscore reports). The play list must render this rather than
                // recompute from play clocks: a drive ends at the next drive's
                // start (closing inter-drive gaps), and the opening drive of a
                // half is pre-rolled to period start — neither is captured by
                // (driveStartPlay.time - driveEndPlay.time).
                play.driveTotalTime = drive ? drive.totalTime : null;
                play.driveEndTimeNeedsInput =
                    !!drive &&
                    isNumber(drive.startTime) &&
                    isNumber(drive.endTime) &&
                    drive.startTime === drive.endTime &&
                    drive.startPeriod === drive.endPeriod;
            } else {
                play.driveEndTime = null;
                play.driveEndPeriod = null;
                play.driveTotalTime = null;
                play.driveEndTimeNeedsInput = false;
            }
            play.canInsertBefore = this._isInsertable(
                play,
                plays,
                PlayInsertionType.Before
            );
            play.canInsertAfter = this._isInsertable(
                play,
                plays,
                PlayInsertionType.After
            );
            play.canReplace = this._isInsertable(
                play,
                plays,
                PlayInsertionType.Replace
            );
            play.canEditKickReceiving = this._canEditKickReceiving(
                play,
                lastCompletePlay,
                gameContext
            );
            //
            return play;
        });
    }

    _summarizePlay(play, gameContext) {
        const summary = FootballStringifier.summarize(play, gameContext);
        play.title = summary.title;
        play.messages = summary.messages;
        play.details = summary.details;

        return play;
    }

    async _withMessages(plays, gameId) {
        await this.refreshPlayerCache(gameId);
        const gameContext = await ContextService.getGameContext(gameId);

        return plays.map((play) => this._summarizePlay(play, gameContext));
    }

    async generatePlaySummary(jsonPlay) {
        //Do we need to refresh cache here?
        await this.refreshPlayerCache(jsonPlay.gameId);
        const gameContext = ContextService.getGameContext(jsonPlay.gameId);

        const originalPlay = await this.getPlay(jsonPlay.id);
        Object.assign(originalPlay, {
            fragments: originalPlay.fragments.map((f) => {
                const epFIndex = jsonPlay.fragments.findIndex(
                    (epF) => epF.id === f.id
                );
                if (epFIndex >= 0) {
                    return Object.assign(f, jsonPlay.fragments[epFIndex]);
                }

                return f;
            })
        });

        return FootballStringifier.summarize(originalPlay, gameContext);
    }

    async getGamePlays(gameId) {
        // Self-heal legacy games whose play->drive links were corrupted by the
        // old unscoped kickoff/penalty sweep (foreign-driveId penalties produce
        // phantom or missing drive-start badges). Idempotent: a clean or
        // already-repaired game makes no writes.
        await DriveService.repairDriveAssignments(gameId);
        // Self-heal games recorded under an up-counting clock (ascending
        // Clock Type wrongly applied to the clock in 0.11.7–0.11.9): their
        // clock-fed times were stored as elapsed instead of the canonical
        // remaining form. Idempotent: a clean game makes no writes.
        await this.repairUpCountingClockData(gameId);

        const plays = await playRepository.getPlaysWithFragments(gameId);
        const activeDrive = await DriveService.getCurrentDrive(gameId);
        let gameContext = await ContextService.getGameContext(gameId);
        const drives = await DriveService.getByGameId(gameId);

        // A paused running clock must never show an earlier time than the
        // last recorded play — restore it from the plays when it does.
        gameContext = await this._restoreClockFromPlays(gameContext, plays);

        // Recompute drive time-of-possession the same way the boxscore does so
        // the per-drive total surfaced on the play list matches the boxscore
        // exactly (gap-closed end times + opening-drive pre-roll). In-memory
        // only — these instances are not persisted here.
        DriveService.computeDriveTotalTimes(drives, gameContext);

        // Start of Drive plays are no longer created. Legacy games still
        // holding one (not yet normalized on ingestion) must not surface it
        // in the play-by-play list.
        const visiblePlays = plays.filter(
            (p) => !p.historyContains(FootballState.StartDrive)
        );

        return this._withFlags(
            await this._withMessages(visiblePlays, gameId),
            activeDrive,
            gameContext,
            drives
        );
    }

    /**
     * Restore a paused running clock from the last recorded play time.
     * The clock only counts down, so a persisted value ABOVE the last
     * play's stamped time is always stale — a pre-tick anchor persisted
     * by a concurrent writer during the play sequence, a sync pull from
     * another device, or the stale-state freeze in GameClock.fromJSON.
     * The plays are the authoritative record of game time; adopt the
     * newest complete play's time from the current period. Idempotent;
     * no-op for manual clocks, ticking clocks, and consistent states.
     * Deliberate upward corrections are safe: setting the clock via the
     * Time dialog records a SetTimeAndPeriod play, which then IS the
     * last play.
     * @param {FootballGameContext} gameContext
     * @param {FootballPlay[]} plays - the game's plays, index-ascending
     * @returns {Promise<FootballGameContext>} the (possibly updated) context
     * @private
     */
    async _restoreClockFromPlays(gameContext, plays) {
        const clock = gameContext?.gameClock;
        const restorable =
            gameContext?.clockType === "running" &&
            clock &&
            typeof clock.isPaused === "function" &&
            clock.isPaused();
        if (!restorable) {
            return gameContext;
        }

        const currentPeriod = gameContext.getCurrentPeriod();
        const lastTimedPlay = [...plays]
            .reverse()
            .find(
                (play) =>
                    play.isComplete() &&
                    typeof play.time === "number" &&
                    play.period === currentPeriod
            );
        if (!lastTimedPlay || lastTimedPlay.time >= clock.now()) {
            return gameContext;
        }

        clock.setSeconds(lastTimedPlay.time / 1000);
        return ContextService.updateContext(gameContext);
    }

    /**
     * One-shot recovery for games recorded under an up-counting clock
     * (see up-clock-recovery.js). Converts affected plays' clock-fed
     * times (fragments, snapshots, drives) back to canonical remaining
     * milliseconds and persists them. Gated on the per-play snapshot
     * direction marker, which is flipped on conversion — a healthy or
     * already-recovered game makes no writes.
     * @param {string} gameId
     */
    async repairUpCountingClockData(gameId) {
        const plays = await playRepository.getPlaysWithFragments(gameId);
        const drives = await DriveService.getByGameId(gameId);

        const { converted, convertedPlayIds, skippedDriveIds } =
            recoverUpCountingClockData({ plays, drives });

        if (converted) {
            const convertedSet = new Set(convertedPlayIds);
            for (const play of plays) {
                if (convertedSet.has(play.id)) {
                    await playRepository.put(play);
                    if (play.fragments?.length) {
                        await fragmentRepository.bulkPut(play.fragments);
                    }
                }
            }
            await driveRepository.bulkPut(drives);
            // Support breadcrumb: converted games should be spot-checked
            // (hand-typed times in the affected window convert like
            // clock-fed ones).
            console.warn(
                `Recovered up-counting clock data for game ${gameId}: ` +
                    `${convertedPlayIds.length} plays converted` +
                    (skippedDriveIds.length
                        ? `; drives needing manual review: ${skippedDriveIds.join(
                              ", "
                          )}`
                        : "")
            );
        }
    }

    async getFragments(playId) {
        return fragmentRepository.where({ playId }).toArray();
    }

    async removeOrphanFragments(play) {
        const states = play.getHistory().map((s, index) => {
            return {
                name: s.name,
                index
            };
        });
        const playFragments = await this.getFragments(play.id);
        const fragmentsToRemove = playFragments.filter(
            (f) =>
                !states.find(
                    (s) =>
                        f.associatedState === s.name &&
                        f.associatedHistoryIndex === s.index
                )
        );

        await fragmentRepository.bulkDelete(fragmentsToRemove.map((f) => f.id));
    }

    async goBackTo(playId, historyIndex) {
        const play = await playRepository.getById(playId);

        await play.goBackTo(historyIndex);
        await playRepository.put(play);

        await this.removeOrphanFragments(play);

        return this.getPlay(play.id);
    }

    async getOrCreatePlay(gameId) {
        const gamePlays = await this.getGamePlays(gameId);
        const gameContext = await ContextService.getGameContext(gameId);

        gameContext.resetTriggers();
        await ContextService.updateContext(gameContext);

        let play = gamePlays.find((p) => !p.isComplete());
        if (!play) {
            play = await this.addPlay(gameId);
        }

        return this._getPlayResult(play);
    }

    async getGameTeams(gameId) {
        const game = await gameRepository.getById(gameId);
        const event = await eventRepository.getById(game.eventId);
        const { teams } = event;
        const homeTeam = await TeamService.getTeam(teams.homeTeam.id);
        const awayTeam = await TeamService.getTeam(teams.awayTeam.id);

        return { homeTeam, awayTeam };
    }

    async getFragment(playId, stateName, associatedHistoryIndex) {
        const fragments = await fragmentRepository.where({ playId }).toArray();
        return fragments.find(
            (f) =>
                f.associatedState === stateName &&
                f.associatedHistoryIndex === associatedHistoryIndex
        );
    }

    async _getOrCreateFragment(play, fragmentData) {
        const { associatedState, associatedHistoryIndex } = fragmentData;

        let fragment = await this.getFragment(
            play.id,
            associatedState,
            associatedHistoryIndex
        );

        if (!fragment) {
            const gameContext = await ContextService.getGameContext(
                play.gameId
            );
            fragment = FragmentFactory.build(play.id, fragmentData, {
                sportCode: play.getSportCode(),
                collectTime: play.shouldCollectTime(gameContext.gameConfig),
                ruleSetId: gameContext.ruleSetId,
                play
            });
        } else {
            Object.assign(fragment, fragmentData);
        }

        if (fragment) {
            await fragmentRepository.put(fragment);
        }

        return fragment;
    }

    async getMetadata() {
        const transitionMetadata = FootballTransitionConfig.map((config) => {
            return Object.assign(
                config,
                TransitionBuilder.getByName(config.name)
            );
        });

        return {
            transitions: transitionMetadata
        };
    }

    async fragmentUpdateChangesSequence(fragment) {
        const originalFragment = await fragmentRepository.getById(fragment.id);
        return originalFragment.dirtiesPlaySequence(fragment);
    }

    async saveFragment(fragment) {
        const changesSequence = await this.fragmentUpdateChangesSequence(
            fragment
        );
        if (changesSequence) {
            // If fragment's change derives in removing all states after its associated state,
            // then we have to go back to the previous state and start a new transition leaving this fragment as pending.
            const { playId, associatedHistoryIndex, associatedTransition } =
                fragment;
            const previousIndex =
                associatedHistoryIndex > 0 ? associatedHistoryIndex - 1 : 0;
            if (previousIndex) {
                await this.goBackTo(playId, previousIndex);
                const { play } = await this.startTransition(
                    playId,
                    associatedTransition
                );
                const pendingFragment = play.getPendingFragment();
                const { id } = pendingFragment;
                const updatedFragment = Object.assign(
                    pendingFragment,
                    fragment,
                    { id }
                );
                await fragmentRepository.put(updatedFragment);
            }
        } else {
            await fragmentRepository.put(fragment);

            let play = await this.getPlay(fragment.playId);

            if (play.canEndTransition(fragment)) {
                await this.endTransition(
                    fragment.playId,
                    fragment.associatedTransition,
                    fragment
                );
            } else if (fragment.hasRequiredData()) {
                // Save scenarios involving fragments that use requiredAfterNextState requiredData
                await this._attemptSingleTransition(play);
            }
        }

        await this._reevaluateTimeCollection(fragment.playId);

        const savedPlay = await this.getPlay(fragment.playId);

        return this._getPlayResult(savedPlay);
    }

    /**
     * Re-evaluate whether an EndOfPlay fragment should be upgraded to
     * CollectDriveTime. This handles the case where scoring status (e.g. TD)
     * can only be determined after field position data is saved on a prior
     * fragment (e.g. Rush after Tackle).
     *
     * The endOfPlay transition may have already auto-ended (because EndOfPlay
     * has no requiredData), leaving the FSM at END_OF_PLAY. We replace the
     * fragment in-place without rewinding the FSM — finalizePlay() can still
     * transition from END_OF_PLAY to FINALIZE normally.
     */
    async _reevaluateTimeCollection(playId) {
        const play = await this.getPlay(playId);

        const endOfPlayFragment = play.fragments.find(
            (f) =>
                f.associatedState === FootballState.EndOfPlay &&
                !(f instanceof CollectDriveTime)
        );

        if (!endOfPlayFragment) return;

        const gameContext = await ContextService.getGameContext(play.gameId);
        if (!play.shouldCollectTime(gameContext.gameConfig)) return;

        // Replace EndOfPlay with CollectDriveTime in-place (same DB ID).
        // The FSM remains at END_OF_PLAY — no rewind needed.
        // CollectDriveTime's requiredData will cause the wizard to prompt
        // for time entry. Finalization proceeds via finalizePlay().
        const collectTime = new CollectDriveTime();
        Object.assign(collectTime, {
            id: endOfPlayFragment.id,
            playId: endOfPlayFragment.playId,
            associatedState: endOfPlayFragment.associatedState,
            associatedHistoryIndex: endOfPlayFragment.associatedHistoryIndex,
            associatedTransition: endOfPlayFragment.associatedTransition
        });

        await fragmentRepository.put(collectTime);
    }

    async setStartSettings(
        gameId,
        { offenseTeam, tossWinner, swapTeams },
        gameConfig
    ) {
        let gameContext = await ContextService.getGameContext(gameId);
        const {
            firstDownFlagStyle,
            fieldLength,
            minutesByPeriod,
            teamZoneQty
        } = gameConfig.general;
        const totalZones = teamZoneQty * 2;
        const isZoneToGain =
            firstDownFlagStyle === FirstDownStyles.ZoneToGain.code;
        // The integer-key filter intentionally keeps only whole-yardline zone
        // boundaries. When fieldLength is not divisible by totalZones the
        // fractional boundaries are dropped, e.g. a 50yd field (50/4=12.5)
        // yields [25, 50] (2 zones of 25) rather than fractional [12.5, 25,
        // 37.5, 50]. Do not switch this to rounding without re-confirming the
        // expected flag-football zone layout for non-multiple field lengths.
        const yardSet = Array.from(Array(fieldLength + 1).keys()).filter(
            (key) => key % (fieldLength / totalZones) === 0 && key !== 0
        );
        // teamZoneQty (zones per half) must match the zones actually produced
        // above. When fractional boundaries are dropped (e.g. 50yd -> [25, 50],
        // 2 zones) the configured teamZoneQty (2 -> 4 zones) is stale, which
        // makes the UI render phantom zone marks (12.5/37.5). Re-derive it from
        // the real yardSet so consumers draw only the actual zone boundaries.
        const actualTeamZoneQty = isZoneToGain
            ? Math.max(1, yardSet.length / 2)
            : teamZoneQty;
        // When the crew has hand-configured the first-down spots, preserve them
        // instead of overwriting with the uniformly-computed yardSet. The custom
        // set may be non-uniform, so teamZoneQty is derived from its length.
        const useCustomFirstDown =
            isZoneToGain && gameConfig.general.customFirstDownYardSet;
        const resolvedFirstDownYardSet = useCustomFirstDown
            ? gameConfig.general.firstDownYardSet
            : isZoneToGain
            ? yardSet
            : gameContext.gameConfig.general.firstDownYardSet;
        const resolvedTeamZoneQty = useCustomFirstDown
            ? Math.max(1, resolvedFirstDownYardSet.length / 2)
            : actualTeamZoneQty;
        // The event configuration at game start is authoritative for the
        // clock type — the crew may have changed the Running Clock setting
        // after the context was created (context creation happens when the
        // game screen is first opened, before the coin toss). A config
        // without the flag means manual; the context's clockType is derived
        // state and never consulted.
        const runningClock = gameConfig.clock?.runningClock === true;
        const clockType = runningClock ? "running" : "manual";
        const gameClock = ClockFactory.create({
            type: clockType,
            limitSeconds: getPeriodStartSeconds(minutesByPeriod)
        });
        gameContext = await ContextService.updateContext(
            Object.assign(gameContext, {
                startSettings: {
                    offenseTeam,
                    tossWinner
                },
                gameClock,
                clockType,
                gameConfig: {
                    ...gameConfig,
                    general: {
                        ...gameConfig.general,
                        firstDownYardSet: resolvedFirstDownYardSet,
                        teamZoneQty: resolvedTeamZoneQty
                    }
                },
                swapTeams,
                gameInitialized: true
            })
        );

        return { gameContext };
    }

    async createDrivePlay(gameId) {
        const { play } = await this.getOrCreatePlay(gameId);

        // A coin toss still pending on the current play means a drive cannot
        // start yet (possession is undecided).
        if (!play.getFirstFragment(FootballState.CoinToss)) {
            return this._startDriveWithoutPlay(gameId);
        }
    }

    /**
     * Open a new drive without inserting a synthetic "Start of Drive" play.
     * Creates the Drive object, links it as the current drive, and assigns
     * possession to the receiving/possessing team. A kickoff (or any play)
     * that ends in the endzone is a return touchdown: the drive is the return
     * itself, so the extra point is armed, the ball is moved to the PAT spot,
     * and the drive is closed with the score. This mirrors the rule pass the
     * old StartDrive play used to perform (CreateDrive.kickoffContextChanges +
     * PatFieldSetup + EndDrive).
     * @param {string} gameId
     * @returns {Promise<FootballGameContext>}
     * @private
     */
    async _startDriveWithoutPlay(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);

        const drive = await DriveService.startDrive(gameContext);
        gameContext.currentDriveId = drive.id;
        gameContext.setDriveContext({
            ...gameContext.driveContext,
            triggerDrive: false,
            driveTeamId: gameContext.getPossessionTeamId()
        });

        // A kickoff/return that ends in the endzone with no drive to attribute
        // it to is a return touchdown. Gate on the touchdown end type so a
        // kickoff-return safety (ball also at the goal line) is not mistaken
        // for a score.
        const endedInTouchdown = DriveEndType.Touchdown.equals(
            gameContext.driveContext.previousDriveEndType
        );
        const lastWasTd =
            endedInTouchdown &&
            ((gameContext.isVisitorPossession() &&
                gameContext.fieldPosition <= 0) ||
                (gameContext.isHomePossession() &&
                    gameContext.fieldPosition >= gameContext.getFieldLength()));
        if (lastWasTd) {
            gameContext.triggerPAT = true;
            await DriveService.endDrive(gameContext, {
                endSpot: gameContext.getFieldPosition()
            });
            const spot = gameContext.gameConfig.spots.PATTry;
            const patFieldPosition = !gameContext.homeTeamHasPossession
                ? spot
                : gameContext.getFieldLength() - spot;
            gameContext.setFieldPosition(patFieldPosition);
            gameContext.currentDriveId = null;
        }

        return ContextService.updateContext(gameContext);
    }

    async _createPendingPATPlay(gameId, time) {
        const play = await this.addPlay(gameId, time);
        return this.startTransition(play.id, FootballTransition.PatTry);
    }

    async _createPendingKickOffPlay(gameContext) {
        const play = await this.addPlay(gameContext.gameId, gameContext.time);
        return this.startTransition(play.id, FootballTransition.Kick);
    }

    async createGameStartPlay(gameId, tossWinner, offenseTeam) {
        const { play } = await this.getOrCreatePlay(gameId);

        play.gameControl()
            .gameStart({
                tossWinner,
                offenseTeam
            })
            .finalize();

        await this.savePlay(play);
        return this._processPlay(play.id);
    }

    async startGame(gameId, startSettings) {
        const gameContext = await ContextService.getGameContext(gameId);

        if (!gameContext.isGameInitialized()) {
            const gameConfiguration = await this.getEventConfigurationByGameId(
                gameId
            );
            await this.setStartSettings(
                gameId,
                startSettings,
                gameConfiguration
            );
            await this.createStartPlays(
                gameId,
                startSettings,
                gameConfiguration
            );
        }

        return this._getPlayResult({ gameId, index: 0 });
    }

    async createStartPlays(gameId, startGameSettings) {
        const { tossWinner, offenseTeam } = startGameSettings;

        const gameStartPlay = await this.createGameStartPlay(
            gameId,
            tossWinner,
            offenseTeam
        );
        const gameContext = await ContextService.getGameContext(gameId);
        // No kickoff to start the game means the first drive opens immediately
        // (the Drive object is created without a synthetic Start of Drive play).
        if (!gameContext.triggerKO) {
            await this.createDrivePlay(gameId);
        }
        const { play: firstPlay } = await this.getOrCreatePlay(gameId);

        return { plays: [firstPlay, gameStartPlay] };
    }

    async _createStartPeriodPlay(gameId, period) {
        const { play } = await this.getOrCreatePlay(gameId);

        const gameConfiguration = await this.getEventConfigurationByGameId(
            gameId
        );
        const { minutesByPeriod } = gameConfiguration.general;
        const periodLengthInSeconds = getPeriodStartSeconds(minutesByPeriod);

        play.gameControl()
            .startPeriod({
                period,
                periodLengthInSeconds
            })
            .finalize();

        await this.savePlay(play);
        return this._processPlay(play.id);
    }

    async getPlaysWithState(gameId, state) {
        const plays = await playRepository.getPlaysWithFragments(gameId);
        return plays.filter((p) =>
            p.fragments.some((f) => f.associatedState === state)
        );
    }

    async getEventConfigurationByGameId(gameId) {
        const { eventId } = await gameRepository.getById(gameId);
        const { gameConfiguration } =
            await EventService.getGameConfigurationOrDefault(eventId, true);

        return gameConfiguration;
    }

    async _createSecondHalfDrivePlay(gameId, period) {
        const gameContext = await ContextService.getGameContext(gameId);
        const startOfSecondHalf = gameContext.isStartOfSecondHalf(period);
        if (startOfSecondHalf) {
            await this._finalizeDrive(gameContext);

            if (!gameContext.triggerKO)
                return await this.createDrivePlay(gameId);
        }

        return null;
    }

    async _createFirstDriveOfPeriod(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);
        await this._finalizeDrive(gameContext);
        if (!gameContext.triggerKO) return await this.createDrivePlay(gameId);
    }

    async _createOvertimeCoinTossPlay(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);
        if (gameContext.getOvertimePeriod() === 1) {
            await this._finalizeDrive(gameContext);
            const { play } = await this.getOrCreatePlay(gameId);

            play.gameControl();

            await this.savePlay(play);
            await this.startTransition(play.id, FootballTransition.CoinToss);
        }
    }

    async _createChangePeriodPlays(gameId, period) {
        await this._createStartPeriodPlay(gameId, period);
        await this._createSecondHalfDrivePlay(gameId, period);
        await this._createOvertimeCoinTossPlay(gameId);
    }

    async setPeriod(gameId, period) {
        const { play } = await this.getOrCreatePlay(gameId);

        const gameContext = await ContextService.getGameContext(gameId);
        const currentPeriod = gameContext.getCurrentPeriod();
        const nextPeriod = currentPeriod + 1;
        if (period === nextPeriod) {
            await this._createChangePeriodPlays(gameContext.gameId, period);
        } else {
            throw new GamePeriodChangeError();
        }

        return this._getPlayResult(play);
    }

    async _setTime(gameId, seconds) {
        let gameContext = await ContextService.getGameContext(gameId);
        gameContext.gameClock.setSeconds(seconds);
        return ContextService.updateContext(gameContext);
    }

    /**
     * Generate a new and un-finalized SetTimeAndPeriod play.
     * @param {string} gameId
     * @param {number} time
     * @param {number} period
     * @returns {Promise<FootballPlay>}
     */
    async initTimeAndPeriodPlay(gameId, time, period) {
        const newTime = await this._calculateNewTimeInSeconds(
            gameId,
            time,
            period
        );
        let { play } = await this.getOrCreatePlay(gameId);
        if (!play.isInitialStep()) {
            play = await this.goBackTo(play.id, 1);
        }
        play.gameControl().setTimeAndPeriod({
            timeAndPeriod: { milliseconds: newTime * 1000, period }
        });
        return play;
    }

    async _createTimeAndPeriodPlay(gameId, time, period) {
        const play = (
            await this.initTimeAndPeriodPlay(gameId, time, period)
        ).finalize();
        await this.savePlay(play);
        return this._processPlay(play.id);
    }

    async setTimeAndPeriod(gameId, seconds, period) {
        const game = await GameService.getGame(gameId);
        const lastPlay = (await this.getGamePlays(gameId))?.pop();

        await ValidationService.canSetClockTime(game, seconds, period);

        const gameContext = await this._setTime(gameId, seconds);
        let timeAndPeriodPlay = lastPlay;
        if (period === gameContext.getCurrentPeriod()) {
            timeAndPeriodPlay = await this._createTimeAndPeriodPlay(
                gameId,
                seconds,
                period
            );
        } else if (period > gameContext.getCurrentPeriod()) {
            await this.setPeriod(gameId, period);
        }

        return this._getPlayResult(timeAndPeriodPlay);
    }

    /**
     * Set the clock at which a drive ended. A drive boundary is shared — a
     * drive's end is the next drive's start — so this stamps the end on the
     * given drive AND the start on the following drive (if any), keeping the
     * two consistent and the time-of-possession split between them correct.
     * Needed because plays that change possession (turnover, downs, fumble)
     * do not collect time, so the boundary otherwise defaults to a stale clock.
     * @param {string} gameId
     * @param {string} driveId
     * @param {number} seconds  clock value, in seconds, when the drive ended
     * @param {number} period
     * @returns {Promise<Array>} the refreshed play list
     */
    async setDriveEndTime(gameId, driveId, seconds, period) {
        const gameConfiguration = await this.getEventConfigurationByGameId(
            gameId
        );
        const { minutesByPeriod } = gameConfiguration.general;

        const drive = await DriveService.getById(driveId);
        await ValidationService.canSetDriveEndTime(
            drive,
            seconds,
            period,
            minutesByPeriod
        );

        await this._applyDriveEndTime(
            drive,
            seconds * 1000,
            period,
            minutesByPeriod
        );

        return this.getGamePlays(gameId);
    }

    /**
     * Stamp the shared drive boundary: a drive's end == the next drive's start.
     * Sets the drive's end and the following drive's start to the same instant
     * and recomputes both totals. Used by `setDriveEndTime` and by play-time
     * edits on a drive-ending play (the drive end is inferred from play time).
     * @private
     */
    async _applyDriveEndTime(drive, endTime, period, minutesByPeriod) {
        if (!drive) return;
        const nextDrive = await DriveService.getNextDrive(drive.id);

        await DriveService.setEndTimeToDrive(
            drive,
            endTime,
            period,
            minutesByPeriod
        );

        if (nextDrive) {
            nextDrive.startTime = endTime;
            nextDrive.startPeriod = period;
            if (isNumber(nextDrive.endTime) && isNumber(nextDrive.endPeriod)) {
                // Recompute the next drive's total from its new start.
                await DriveService.setEndTimeToDrive(
                    nextDrive,
                    nextDrive.endTime,
                    nextDrive.endPeriod,
                    minutesByPeriod
                );
            } else {
                await DriveService.saveDrive(nextDrive);
            }
        }
    }

    /**
     * Stamp a drive's start (== the previous drive's end) from a boundary play
     * (e.g. an edited kickoff time). Recomputes this drive's total and the
     * previous drive's end/total.
     * @private
     */
    async _applyDriveStartTime(drive, startTime, period, minutesByPeriod) {
        if (!drive) return;
        const prevDrive = await DriveService.getPreviousDrive(drive.id);

        drive.startTime = startTime;
        drive.startPeriod = period;
        if (isNumber(drive.endTime) && isNumber(drive.endPeriod)) {
            await DriveService.setEndTimeToDrive(
                drive,
                drive.endTime,
                drive.endPeriod,
                minutesByPeriod
            );
        } else {
            await DriveService.saveDrive(drive);
        }

        if (prevDrive) {
            await DriveService.setEndTimeToDrive(
                prevDrive,
                startTime,
                period,
                minutesByPeriod
            );
        }
    }

    /**
     * After a play's time changes, keep the drive boundary it governs in sync.
     * The drive end is inferred from play time: the LAST play of a drive sets
     * that drive's end (== next drive's start); the FIRST play sets that drive's
     * start (== previous drive's end). Mid-drive plays touch no boundary.
     * @private
     */
    async _reconcileDriveBoundaryForPlay(play, milliseconds, period) {
        if (!play.driveId) return;

        const { minutesByPeriod } = (
            await this.getEventConfigurationByGameId(play.gameId)
        ).general;

        const drives = await DriveService.getByGameId(play.gameId, true);
        const drive = drives.find((d) => d.id === play.driveId);
        const drivePlays = drive?.plays || [];
        if (!drive || !drivePlays.length) return;

        if (drivePlays[drivePlays.length - 1].id === play.id) {
            await this._applyDriveEndTime(
                drive,
                milliseconds,
                period,
                minutesByPeriod
            );
        }
        if (drivePlays[0].id === play.id) {
            await this._applyDriveStartTime(
                drive,
                milliseconds,
                period,
                minutesByPeriod
            );
        }
    }

    async _calculateNewTimeInSeconds(gameId, seconds, period) {
        let newTime = seconds;

        let gameContext = await ContextService.getGameContext(gameId);
        if (period > gameContext.getCurrentPeriod()) {
            const gameConfiguration = await this.getEventConfigurationByGameId(
                gameId
            );
            const { minutesByPeriod } = gameConfiguration.general;
            const periodLengthInSeconds =
                getPeriodStartSeconds(minutesByPeriod);
            newTime = periodLengthInSeconds;
        }

        return newTime;
    }

    async finalizePlay(playId) {
        return this.startTransition(playId, FootballTransition.Finalize);
    }

    async getCurrentTimeAndPeriod(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);

        return {
            period: gameContext.getCurrentPeriod(),
            time: getSafeOrDefault(() => gameContext.gameClock.now(), 0)
        };
    }

    async finalizeGame(gameId, finalizeSettings) {
        const game = await gameRepository.getById(gameId);
        const { eventId } = game;
        const event = await this._finalizeEvent(eventId);

        const { notes, ...additionalSettings } = finalizeSettings;
        const gameContext = await this._finalizeGameContext(
            gameId,
            additionalSettings
        );
        const drive = await DriveService.getLastDrive(gameContext.gameId);
        if (drive) {
            // Stamp the last drive's end clock at the current finalize-time
            // game clock regardless of completion state. No next drive will
            // come to retroactively close the gap, so the last drive must
            // own all clock time through finalize.
            await DriveService.setEndTimeToDrive(
                drive,
                gameContext.gameClock.now(),
                gameContext.currentPeriod,
                gameContext.gameConfig.general.minutesByPeriod
            );
        }
        await this._finalizeDrive(gameContext);

        event.notes = sanitizeInvalidCharacters(notes);
        event.score.home = gameContext.getHomeScore();
        event.score.away = gameContext.getVisitorScore();
        await GamedayApiService.saveOrUpdateEvent(event);

        return {
            event,
            gameContext: FootballSerializer.serializeGameContext(gameContext)
        };
    }

    async saveFinalizationSettings(gameId, finalizeSettings) {
        const { notes } = finalizeSettings;

        const game = await gameRepository.getById(gameId);
        let gameContext = await ContextService.getGameContext(gameId);
        gameContext.finalizeSettings = finalizeSettings;
        gameContext = await ContextService.updateContext(gameContext);

        const event = await eventRepository.getById(game.eventId);
        event.notes = sanitizeInvalidCharacters(notes);
        await GamedayApiService.saveOrUpdateEvent(event);

        return {
            event,
            gameContext: FootballSerializer.serializeGameContext(gameContext)
        };
    }

    async _finalizeEvent(eventId) {
        return EventService.setEventStatus(eventId, EventStatusCode.FINAL);
    }

    async _finalizeGameContext(gameId, finalizeSettings) {
        let gameContext = await ContextService.getGameContext(gameId);
        gameContext.setDriveContext({
            previousDriveEndType: DriveEndType.EndOfGame
        });
        gameContext.finalizeSettings = finalizeSettings;

        return ContextService.updateContext(gameContext);
    }

    async _collectMissingGameData(gameId) {
        const game = await GameService.getGame(gameId);
        const event = await EventService.getById(game.eventId);
        const { teams } = event;
        const { homeTeam, awayTeam } = teams;

        const filteredUniforms = [
            TeamPlayer.getUniform(),
            NoPlayer.getUniform()
        ];

        const [homePlayers, awayPlayers] = await Promise.all([
            PlayerService.getPlayers(homeTeam.id, filteredUniforms),
            PlayerService.getPlayers(awayTeam.id, filteredUniforms)
        ]);

        const missingGameData = [];

        if (!homePlayers.length) {
            missingGameData.push({ message: "Home team roster is empty" });
        }

        if (!awayPlayers.length) {
            missingGameData.push({ message: "Away team roster is empty" });
        }

        return missingGameData;
    }

    async isGameDataComplete(gameId) {
        const [plays, missingGameData] = await Promise.all([
            this.getGamePlays(gameId),
            this._collectMissingGameData(gameId)
        ]);
        const completedPlays = plays.filter((p) => p.isComplete());

        return {
            isComplete: !missingGameData.length && !!completedPlays.length,
            missingGameData
        };
    }

    async getPlayers(externalTeamId, includeDeleted = false) {
        return GamedayApiService.getPlayers(externalTeamId, includeDeleted);
    }

    async setOvertimeNotification(gameId, value) {
        const gameContext = await ContextService.setOvertimeNotification(
            gameId,
            value
        );

        return FootballSerializer.serializeGameContext(gameContext);
    }

    async swapTeams(gameId, swap) {
        const gameContext = await ContextService.swapTeams(gameId, swap);

        return FootballSerializer.serializeGameContext(gameContext);
    }

    /**
     * Toggle the ordering players by position flag
     * @param {string} gameId
     * @param {boolean} order
     * @returns {Promise<*>}
     */
    async orderPlayersByPosition(gameId, order) {
        const gameContext = await ContextService.orderPlayersByPosition(
            gameId,
            order
        );
        return FootballSerializer.serializeGameContext(gameContext);
    }

    async keepSwappingTeams(gameId, swap) {
        const gameContext = await ContextService.keepSwappingTeams(
            gameId,
            swap
        );

        return FootballSerializer.serializeGameContext(gameContext);
    }

    async confirmPeriodNotification(gameId) {
        const gameContext = await ContextService.confirmPeriodNotification(
            gameId
        );

        return FootballSerializer.serializeGameContext(gameContext);
    }

    _sortAndFilterTransitions(transitions, state) {
        transitions.sort((a, b) => {
            return a.order && b.order && a.order[state] && b.order[state]
                ? a.order[state] - b.order[state]
                : 0;
        });

        return transitions.filter(
            (t) => t.name !== FootballTransition.Finalize && !t.disabled
        );
    }

    assignTeam(t, previousFragmentTeamId, homeTeam, awayTeam) {
        if (t.recoversPossession === RecoversPossessionSetting.Yes)
            t.team =
                previousFragmentTeamId === homeTeam.id ? homeTeam : awayTeam;
        if (t.recoversPossession === RecoversPossessionSetting.No)
            t.team =
                previousFragmentTeamId === homeTeam.id ? awayTeam : homeTeam;
    }

    async getAllowedTransitions(playId, fragment) {
        const play = await this.getPlay(playId);
        const teams = await this.getGameTeams(play.gameId);
        const gameContext = await ContextService.getGameContext(play.gameId);
        const coinTossRequired = await this._getCoinTossRequired(
            play,
            gameContext
        );

        return this._getAllowedTransitions(
            play,
            fragment,
            teams,
            coinTossRequired,
            gameContext
        );
    }

    _getAllowedTransitions(
        play,
        fragment,
        teams,
        coinTossRequired,
        gameContext
    ) {
        let transitions = play.getAllowedTransitions();
        let lastFragment = fragment || play.getLastFragment();
        if (lastFragment) {
            const { associatedState } = lastFragment;
            transitions = play.getStateTransitions(associatedState);
            const { homeTeam, awayTeam } = teams;
            const previousFragmentTeamId = play.resolveFragmentTeamId(
                play,
                lastFragment,
                gameContext
            );

            //TODO: hide accordingly and add team icons;
            transitions.forEach((t) => {
                this.assignTeam(t, previousFragmentTeamId, homeTeam, awayTeam);
            });

            transitions = this._getApplicableTransitions(
                gameContext,
                play,
                transitions,
                lastFragment,
                coinTossRequired
            );
        }

        return this._sortAndFilterTransitions(transitions, play.getState());
    }

    _getApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        if (fragment.associatedState === FootballState.AddPlay) {
            transitions = this._hideAddPlayTransitions(
                gameContext,
                transitions
            );
        } else {
            transitions = this._hideApplicableTransitions(
                gameContext,
                play,
                transitions,
                fragment,
                coinTossRequired
            );
        }

        return transitions;
    }

    async _getCoinTossRequired(play, gameContext) {
        const firstOTPeriod =
            gameContext.gameConfig.general.periodsInRegulation + 1;

        const firstOtPlays = (
            await playRepository
                .where({
                    gameId: play.gameId
                })
                .toArray()
        ).filter((p) => p.period === firstOTPeriod);

        return (
            gameContext.currentPeriod === firstOTPeriod &&
            !firstOtPlays.some((play) =>
                play.historyContains(FootballState.CoinToss)
            )
        );
    }

    _hideApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        const { ruleSetId } = gameContext;

        transitions = fragment
            ? play.getStateTransitions(fragment.associatedState)
            : play.getAllowedTransitions();

        if (coinTossRequired) {
            return transitions.filter(
                (transition) => transition.name === FootballTransition.CoinToss
            );
        } else {
            if (!gameContext.getOvertimePeriod()) {
                transitions = transitions.filter((transition) =>
                    gameContext.triggerKO
                        ? transition.name !== FootballTransition.FieldGoal
                        : transition.name !== FootballTransition.KickOff
                );
            }

            if (fragment && !isSafety(play, fragment, gameContext)) {
                transitions = transitions.filter(
                    (t) => FootballTransition.Safety !== t.name
                );
            }

            if (fragment && !isRouge(play, fragment, gameContext)) {
                transitions = transitions.filter(
                    (t) => FootballTransition.Rouge !== t.name
                );
            }

            // Drives are created automatically in the implicit-drive model, so
            // the manual "Start of Drive" action is never offered to operators.
            transitions = transitions.filter(
                (transition) =>
                    transition.name !== FootballTransition.StartDrive
            );

            if (!isCflKickEligible(ruleSetId, play)) {
                transitions = transitions.filter(
                    (transition) =>
                        transition.name !== FootballTransition.CflKick
                );
            }

            if (!isPuntAllowed(ruleSetId)) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.Punt
                );
            }

            // When editing at KickOff state, allow Deuce/BarBall if Indoor ruleset
            // (ignore history that came after the KickOff)
            const isEditingAtKickOff =
                fragment &&
                fragment.associatedState === FootballState.KickOff &&
                ruleSetId === Ruleset.IndoorFootball.code;

            if (!isDeuceEligible(ruleSetId, play) && !isEditingAtKickOff) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.Deuce
                );
            }

            if (!isReboundNetEligible(ruleSetId)) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.HitReboundNet
                );
            }

            if (!isBarBallEligible(ruleSetId, play) && !isEditingAtKickOff) {
                transitions = transitions.filter(
                    (t) => t.name !== FootballTransition.HitGoalpost
                );
            }

            if (!play.shouldPromptSkipKickoff(gameContext)) {
                transitions = transitions.filter(
                    (transition) =>
                        transition.name !== FootballTransition.SkipKickoff
                );
            }

            if (isFieldGoalInelligible(ruleSetId, play)) {
                transitions = transitions.filter(
                    (transition) => transition.name !== FootballTransition.Kick
                );
            }

            if (shouldHideEop(ruleSetId, fragment, play)) {
                transitions = transitions.filter(
                    (transition) =>
                        transition.name !== FootballTransition.EndOfPlay
                );
            }

            return transitions;
        }
    }

    _hideAddPlayTransitions(gameContext, transitions) {
        const {
            triggerKO,
            triggerPAT,
            currentDriveId,
            coinTossRequired,
            ruleSetId
        } = gameContext;
        const allowedKickoffTransitions = [
            FootballTransition.PlayPenalty,
            FootballTransition.Kick,
            FootballTransition.GameControl
        ];
        const allowedPatTransitions = [
            FootballTransition.PatTry,
            FootballTransition.PlayPenalty,
            FootballTransition.GameControl
        ];
        const generalTransitions = [
            FootballTransition.PlayPenalty,
            FootballTransition.Pass,
            FootballTransition.Rush,
            FootballTransition.Punt,
            FootballTransition.Kick,
            FootballTransition.Spike,
            FootballTransition.KneelDown,
            FootballTransition.FumbledSnap,
            FootballTransition.GameControl
        ];
        if (triggerKO) {
            transitions = transitions.filter((t) =>
                allowedKickoffTransitions.some(
                    (transition) => transition === t.name
                )
            );
        } else if (triggerPAT) {
            transitions = transitions.filter((t) =>
                allowedPatTransitions.some(
                    (transition) => transition === t.name
                )
            );
        } else if (!currentDriveId || coinTossRequired) {
            transitions = this._filterGameCtrlTransitions(transitions);
        } else {
            if (gameContext.getOvertimePeriod() >= 1) {
                generalTransitions.push(FootballTransition.PatTry);
            }
            transitions = transitions.filter((t) =>
                generalTransitions.some((transition) => transition === t.name)
            );
        }

        // Filter out Punt for Indoor Football
        if (!isPuntAllowed(ruleSetId)) {
            transitions = transitions.filter(
                (t) => t.name !== FootballTransition.Punt
            );
        }

        return transitions;
    }

    /**
     * Filter the provided FootballTransition array for Game Control and set required flags.
     * @param {FootballTransition[]} transitions
     * @returns {FootballTransition[]}
     * @private
     */
    _filterGameCtrlTransitions(transitions) {
        transitions = transitions
            .filter((t) => t.name === FootballTransition.GameControl)
            .map((t) => ({ ...t, showInToolbar: false })); // This is to make game control action visible if you reset a play that forces you to make a game control action like start drive.
        return transitions;
    }

    _getLastPlayerInPossessionForThePlay(play) {
        let lastPlayer;

        const reverseOrderedFragments = play.fragments.sort(
            (a, b) => b.associatedHistoryIndex - a.associatedHistoryIndex
        );
        for (
            let i = 0;
            i < reverseOrderedFragments.length && !lastPlayer;
            i++
        ) {
            [lastPlayer] = reverseOrderedFragments[i].getPlayers();
        }

        return lastPlayer;
    }

    async _getLastPlayerInPossession(gameId) {
        let lastPlayer;

        const plays = await playRepository.getPlaysWithFragments(gameId);

        const reverseOrderedPlays = plays.sort((a, b) => b.index - a.index);

        for (let i = 0; i < reverseOrderedPlays.length && !lastPlayer; i++) {
            lastPlayer = this._getLastPlayerInPossessionForThePlay(
                reverseOrderedPlays[i]
            );
        }

        return lastPlayer;
    }

    async _doUpdatePatStats({ statsFragment, points }) {
        if (statsFragment) {
            const stats = await statsRepository
                .where({
                    fragmentId: statsFragment.id,
                    category: FootballStatsCategory.PAT_COMPLETE
                })
                .toArray();

            if (stats.length) {
                return statsRepository.bulkPut(
                    stats.map((s) =>
                        Object.assign(s, {
                            values: {
                                ...s.values,
                                points
                            }
                        })
                    )
                );
            }
        }

        return [];
    }

    async updatePatStats(originalPlay, originalFragment, updatedFragment) {
        const gameContext = await ContextService.getGameContext(
            originalPlay.gameId
        );
        const { statsFragment, points } =
            PatCompleteStats._resolvePatStatsFragmentAndPoints(
                originalPlay,
                updatedFragment,
                gameContext
            );

        return this._doUpdatePatStats({ statsFragment, points });
    }

    async _updateFragments(play) {
        const originalPlay = await this.getPlay(play.id);
        const gameContext = await ContextService.getGameContext(play.gameId);

        await Promise.all(
            originalPlay.fragments
                .filter((f) => f.isEditable())
                .map(async (originalFragment) => {
                    const updatedFragment = play.fragments.find(
                        (f) => originalFragment.id === f.id
                    );
                    //Current fragment might be a plain json. Make sure it is a Fragment by creating a copy.
                    const newFragment = FragmentFactory.build(
                        originalPlay.id,
                        updatedFragment,
                        {
                            sportCode: originalPlay.getSportCode(),
                            collectTime: originalPlay.shouldCollectTime(
                                gameContext.gameConfig
                            ),
                            ruleSetId: gameContext.ruleSetId,
                            play: originalPlay
                        }
                    );

                    return this.updateFragment(
                        originalPlay,
                        originalFragment,
                        newFragment
                    );
                })
        );

        return this._reprocessPlayStats(originalPlay.id);
    }

    async editPlay(play) {
        const { gameId } = play;
        const lastPlayerInPossessionBeforeEdit =
            await this._getLastPlayerInPossession(gameId);

        await this._updateFragments(play);
        const updatedPlay = await this.getPlay(play.id);
        const activeDrive = await DriveService.getCurrentDrive(gameId);
        let gameContext = await ContextService.getGameContext(gameId);

        const [withMessages] = this._withFlags(
            await this._withMessages([updatedPlay], gameId),
            activeDrive,
            gameContext
        );

        gameContext = await this.updateContextCurrentPlayerInPossession(
            gameId,
            lastPlayerInPossessionBeforeEdit
        );

        return {
            play: withMessages,
            gameContext: FootballSerializer.serializeGameContext(gameContext)
        };
    }

    async updateContextCurrentPlayerInPossession(
        gameId,
        lastPlayerInPossessionBeforeEdit
    ) {
        let gameContext = await ContextService.getGameContext(gameId);
        const currentPlayerInPossession =
            gameContext.getCurrentPlayerInPossession();

        if (
            currentPlayerInPossession &&
            lastPlayerInPossessionBeforeEdit &&
            currentPlayerInPossession.id === lastPlayerInPossessionBeforeEdit.id
        ) {
            const lastPlayerInPossessionAfterEdit =
                await this._getLastPlayerInPossession(gameId);
            if (
                lastPlayerInPossessionAfterEdit &&
                lastPlayerInPossessionAfterEdit.id !==
                    lastPlayerInPossessionBeforeEdit.id
            ) {
                gameContext.setCurrentPlayerInPossession(
                    lastPlayerInPossessionAfterEdit
                );
                gameContext = await ContextService.updateContext(gameContext);
            }
        }

        return gameContext;
    }

    async updateScoring(originalPlay, originalFragment, currentFragment) {
        const gameContext = await ContextService.getGameContext(
            originalPlay.gameId
        );
        const { gameConfig } = gameContext;
        const originalScoringPoints =
            originalFragment.getScoringPoints(gameConfig);
        const currentScoringPoints =
            currentFragment.getScoringPoints(gameConfig);

        if (currentScoringPoints !== originalScoringPoints) {
            const isHomePossession = originalPlay
                .getContextSnapshot()
                .isHomePossession();
            if (isHomePossession) {
                const updatedScore =
                    gameContext.getHomeScore() -
                    originalScoringPoints +
                    currentScoringPoints;
                gameContext.setHomeScore(updatedScore);
            } else {
                const updatedScore =
                    gameContext.getVisitorScore() -
                    originalScoringPoints +
                    currentScoringPoints;
                gameContext.setVisitorScore(updatedScore);
            }

            return ContextService.updateContext(gameContext);
        }
    }

    /**
     * Retrieve the next play
     * @param {FootballPlay} play
     * @returns {Promise<FootballPlay | null>}
     * @private
     */
    async _getNextPlay(play) {
        return (
            await playRepository.where({ gameId: play.gameId }).toArray()
        ).find((p) => p.index === play.index + 1);
    }

    async _updateNextPlayContext(play, fragment) {
        const { milliseconds, period } = fragment.getTimeAndPeriod();

        // Re-flow the edited clock onto the following plays that merely
        // inherited it (no collected time of their own). The clock does not
        // advance on such plays, so they all share the new boundary value.
        // Stop at the next play that collected its own (authoritative) time or
        // at a period change. Fragments are needed for hasCollectedTime().
        const following = (
            await playRepository.getPlaysWithFragments(play.gameId)
        ).filter((p) => p.index > play.index);

        for (const next of following) {
            if (next.period !== period || next.hasCollectedTime()) {
                break;
            }
            next.time = milliseconds;
            next.getContextSnapshot().setClockSeconds(milliseconds / 1000);
            await playRepository.put(next);
        }
    }

    async _getLastCompletePlay(gameId) {
        const [lastPlay] = (await this.getGamePlays(gameId))
            .filter((p) => p.isComplete())
            .reverse();
        return lastPlay;
    }

    async isLastCompletePlay(playId) {
        const play = await this.getPlay(playId);
        const lastPlay = await this._getLastCompletePlay(play.gameId);
        return play.id === lastPlay.id;
    }

    async updatePlayTime(fragment, play) {
        const { requiredData } = fragment;
        const allowedDataTypes = [
            FragmentDataType.Time,
            FragmentDataType.TimeAndPeriod,
            FragmentDataType.SetTimeAndPeriod
        ];
        const canUpdate = requiredData.some((rd) =>
            allowedDataTypes.some((adt) => rd.type === adt)
        );

        if (canUpdate) {
            const { milliseconds, period } = fragment.getTimeAndPeriod();
            const isLastPlay = await this.isLastCompletePlay(play.id);

            if (isLastPlay) {
                if (period === play.period) {
                    await this._setTime(play.gameId, milliseconds / 1000);
                } else {
                    // TODO: Handle Period change here
                }
            }

            await this._updateNextPlayContext(play, fragment);

            if (fragment.associatedState === FootballState.StartDrive) {
                const drive = await DriveService.getById(play.driveId);
                await DriveService.saveDrive(
                    Object.assign(drive, {
                        startTime: milliseconds,
                        startPeriod: period
                    })
                );
            }

            play.time = milliseconds;
            play.period = period;
            await playRepository.put(play);

            // The drive end is inferred from play time, so moving a boundary
            // play's time moves the drive boundary it governs. (No-op at entry:
            // the following drive isn't created until the play finalizes.)
            await this._reconcileDriveBoundaryForPlay(
                play,
                milliseconds,
                period
            );
        }
    }

    async updateFragment(originalPlay, originalFragment, currentFragment) {
        await this.updatePatStats(
            originalPlay,
            originalFragment,
            currentFragment
        );
        await this.updateScoring(
            originalPlay,
            originalFragment,
            currentFragment
        );
        await fragmentRepository.put(currentFragment);
        await this.updatePlayTime(currentFragment, originalPlay);

        return currentFragment;
    }

    /**
     * Process a play (without saving) to return the resulting stats and gameContext.
     * @param {string} playId
     * @returns {{statLogs: FootballStats[], gameContext: FootballGameContext}}
     */
    async _reprocessPlay(playId) {
        const play = await this.getPlay(playId);
        const originalDriveId = play.driveId;
        const game = await gameRepository.getById(play.gameId);

        const gameContext = await ContextService.getGameContext(game.id);
        // eslint-disable-next-line no-unused-vars
        const { _type, ...contextSnapshot } = play.getContextSnapshot();
        const context = Object.assign(gameContext, contextSnapshot);

        const [eventConfiguration, event] = await Promise.all([
            this.getEventConfigurationByGameId(game.id),
            EventService.getById(game.eventId)
        ]);

        const statLogs = await this.ruleSetProcessor.process(
            context,
            eventConfiguration,
            { play },
            event
        );

        if (play.historyContains(FootballState.StartDrive)) {
            await DriveService.deleteDrive(game.id, play.driveId);
            context.currentDriveId = originalDriveId;
        }

        if (contextSnapshot.triggerPAT || context.triggerPAT) {
            context.currentDriveId = null;
        }

        context.triggerNewPeriodNotification = false;

        return { statLogs, gameContext: context };
    }

    /**
     * Process a play to save the resulting stats and return the stats along with the game context.
     * @param {string} playId
     * @returns {{stats: FootballStats[], gameContext: FootballGameContext}}
     */
    async _reprocessPlayStats(playId) {
        const stats = await statsRepository.where({ playId: playId }).toArray();
        await statsRepository.bulkDelete(stats.map((s) => s.id));
        const { gameContext, statLogs } = await this._reprocessPlay(playId);

        return {
            stats: await FootballStatsService.savePlayStatLogs(
                playId,
                statLogs
            ),
            gameContext
        };
    }

    /**
     * Obtain the Game Context to be used during Play Insertion mode.
     * @param {string} gameId
     * @param {FootballPlay} insertPlay
     * @param {PlayInsertionType} insertType
     * @returns {Promise<FootballGameContext>}
     * @private
     */
    async _getInsertionContext(gameId, insertPlay, insertType) {
        const gameContext = await ContextService.getGameContext(gameId);
        const { _type } = gameContext;

        const isBeforeOrReplace = [
            PlayInsertionType.Before,
            PlayInsertionType.Replace
        ].some((t) => t === insertType);

        if (isBeforeOrReplace) {
            return Object.assign(gameContext, {
                ...insertPlay.getContextSnapshot(),
                _type
            });
        } else if (insertType === PlayInsertionType.After) {
            return (await this._reprocessPlay(insertPlay.id)).gameContext;
        }

        return gameContext;
    }

    /**
     * Create and return a pending play that is intended to replace an originalPlay
     * @param {string} gameId
     * @param {FootballPlay} originalPlay
     * @returns {Promise<FootballPlay|null>}
     * @private
     */
    async _createReplacementPlay(gameId, originalPlay) {
        let { play: replacementPlay } = await this.getOrCreatePlay(gameId);
        if (replacementPlay.isComplete()) return null;

        replacementPlay = Object.assign(replacementPlay, {
            ...originalPlay,
            id: replacementPlay.id,
            index: replacementPlay.index
        });

        if (originalPlay.fsm.state === FootballState.Finalize) {
            replacementPlay.fsm.historyBack();
        }

        const replacementFragments = originalPlay.fragments
            .filter((f) => f.associatedState !== FootballState.AddPlay)
            .map((f) => {
                const { id, ...fragment } = f;
                return Object.assign(new Fragment(), {
                    ...fragment,
                    playId: replacementPlay.id
                });
            });
        await fragmentRepository.bulkPut(replacementFragments);
        await playRepository.put(replacementPlay);
        const { play, gameContext } = await this.getOrCreatePlay(gameId);

        return this._summarizePlay(play, gameContext);
    }

    /**
     * Initialize a pending play for Play Insertion mode.
     * @param {string} gameId
     * @param {FootballGameContext} gameContext
     * @param {PlayInsertionType} insertType
     * @param {FootballPlay} insertPlay The play being inserted upon
     * @returns {Promise<FootballPlay>}
     * @private
     */
    async _getInsertionPlay(gameId, gameContext, insertType, insertPlay) {
        if (insertType === PlayInsertionType.Replace) {
            return this._createReplacementPlay(gameId, insertPlay);
        }

        const { play } = await this.getOrCreatePlay(gameId);
        const contextSnapshot = gameContext.generateSnapshot();
        play.setContextSnapshot(contextSnapshot);
        play.time = contextSnapshot.getTime();
        return this.savePlay(play);
    }

    /**
     * Retrieve an activeStep (if any) for a play insertion
     * @param {FootballPlay} play
     * @param {PlayInsertionType} insertType
     * @returns {null | number}
     * @private
     */
    _getInsertionActiveStep(play, insertType) {
        let activeStep = null;
        if (insertType !== PlayInsertionType.Replace) return null;

        const isDriveStart = play.historyContains(FootballState.StartDrive);
        if (isDriveStart) {
            activeStep = -2;
        } else {
            activeStep = -1;
        }

        return activeStep;
    }

    /**
     * Initialize play insertion mode.
     * @param {string} gameId
     * @param {FootballPlay} insertPlay
     * @param {PlayInsertionType} insertType
     * @returns {Promise<{play: FootballPlay, gameContext: *, activeStep: number | null}>}
     */
    async initPlayInsertion(gameId, insertPlay, insertType) {
        const gameContext = await this._getInsertionContext(
            gameId,
            insertPlay,
            insertType
        );
        const play = await this._getInsertionPlay(
            gameId,
            gameContext,
            insertType,
            insertPlay
        );
        const activeStep = this._getInsertionActiveStep(play, insertType);

        return {
            activeStep,
            play,
            gameContext: FootballSerializer.serializeGameContext(
                await ContextService.updateContext(gameContext)
            )
        };
    }

    /**
     * Attempt to cancel Play Insertion mode, reprocess the last play of the game and restore the resulting context, or return null if no plays exist.
     * @param {string} gameId
     * @returns {Promise<FootballGameContext | null>}
     */
    async cancelPlayInsertion(gameId) {
        const [lastPlay] = (await playRepository.getPlaysWithFragments(gameId))
            .reverse()
            .filter((p) => p.isComplete());
        if (!lastPlay) return null;
        const { gameContext } = await this._reprocessPlay(lastPlay.id);
        await this._reconcileCurrentDrive(gameContext);
        return ContextService.updateContext(gameContext);
    }

    /**
     * Reconcile a recomputed context with the live current drive.
     *
     * Reprocessing the last play runs the rules engine, so a change-of-possession
     * rule (turnover/punt) nulls `currentDriveId` and arms `triggerDrive` as if a
     * fresh drive still has to be created. In the implicit-drive model that drive
     * was already created eagerly when the play was first entered and still exists
     * as the open (incomplete) drive. Re-link it instead of leaving the game
     * stranded waiting for a Start Drive after a reload/cancel.
     * @param {FootballGameContext} gameContext
     * @returns {Promise<void>}
     * @private
     */
    async _reconcileCurrentDrive(gameContext) {
        if (gameContext.currentDriveId) {
            return;
        }
        const currentDrive = await DriveService.getCurrentDrive(
            gameContext.gameId
        );
        if (
            !currentDrive ||
            currentDrive.homeTeamHasPossession !==
                gameContext.homeTeamHasPossession
        ) {
            return;
        }
        gameContext.currentDriveId = currentDrive.id;
        gameContext.setDriveContext({
            ...gameContext.getDriveContext(),
            triggerDrive: false,
            driveTeamId: gameContext.getPossessionTeamId()
        });
    }

    /**
     * Calculate an insertion index.
     * @param {number} startIndex
     * @param {PlayInsertionType} insertType
     * @returns {number}
     * @private
     */
    _getInsertedPlayIndex(startIndex, insertType) {
        const isBeforeOrReplace = [
            PlayInsertionType.Before,
            PlayInsertionType.Replace
        ].some((s) => s === insertType);
        return isBeforeOrReplace ? startIndex : startIndex + 1;
    }

    /**
     * Retrieve the range of play indexes that should be used in the reintegration process.
     * @param {FootballPlay} play
     * @returns {Promise<number[]>} An array of play indexes corresponding to a found drive or an array containing the provided play's index and the index of the last play that a driveId was checked.
     * @private
     */
    async _getReintegrationRange(play) {
        const { index, driveId } = await this._retrieveReintegrationDriveId(
            play
        );

        let range = (await this.getDrivePlays(play.gameId, driveId)).map(
            (dp) => dp.index
        );

        if (!range.length) {
            range = [play.index, index];
        }

        return range
            .filter((num, index) => range.indexOf(num) === index)
            .sort((a, b) => a - b);
    }

    /**
     * Finalize and insert a new play into the game.
     * @param {string} id Corresponds to a saved play that can be finalized.
     * @param {number} startIndex The initial reference index, used for calculating the insertion index.
     * @param {PlayInsertionType} insertType The direction that the new play should be inserted, used for calculating the insertion index.
     * @returns {Promise<{FootballGameContext}>}
     */
    async insertNewPlay(id, startIndex, insertType) {
        let play = Object.assign(await this.getPlay(id), {
            index: this._getInsertedPlayIndex(startIndex, insertType)
        });

        const isReplacingLastPlay =
            insertType === PlayInsertionType.Replace &&
            play.index ===
                (await this._getLastCompletePlay(play.gameId))?.index;

        const { isTurnover, existingPlay } = await this._validateInsert(
            play,
            insertType
        );

        if (insertType === PlayInsertionType.Replace) {
            const { eventId } = await gameRepository.getById(play.gameId);
            await StreamService.updateMarkerPlayIdRefs(
                eventId,
                existingPlay.id,
                play.id
            );
            await this.deletePlay(existingPlay.id);
        }

        play = await this.savePlay(play.finalize());
        await this.propagatePlayIndexes(play, {
            future: true
        });

        const lastDrive = await DriveService.getLastDrive(play.gameId);
        const shouldInsertDrive =
            isTurnover && play.contextSnapshot.currentDriveId === lastDrive?.id;

        const playIndexes = await this._getReintegrationRange(play);

        if (isReplacingLastPlay && shouldInsertDrive) {
            const { index: lastPlayIndex } = await this._getLastCompletePlay(
                play.gameId
            );
            playIndexes.push(lastPlayIndex);
        }

        // A kickoff or PAT carries no driveId, so _getReintegrationRange
        // resolves the range from a neighbouring drive and excludes the
        // inserted play itself. Reintegrating a range that starts *after* the
        // inserted play skips reprocessing it, so the trigger changes its rules
        // apply (a PAT clears triggerPAT + arms the next kickoff; a kickoff
        // clears triggerKO) never propagate to the following plays — leaving
        // the game stuck (e.g. only PAT/penalty offered). Force the inserted
        // play's own index into the range so it is always reprocessed.
        const isKickoff = play.historyContains(FootballState.KickOff);
        const isPat = play.historyContains(FootballState.PatTry);
        if (isKickoff || isPat) {
            playIndexes.push(play.index);
        }

        return this.reintegratePlays(
            play.gameId,
            Math.min(...playIndexes),
            Math.max(...playIndexes)
        );
    }

    /**
     * Determine if an invalid change of possession has occurred, based on the provided insertType
     * @param {FootballPlay} newPlay
     * @param {FootballPlay | null} existingPlay
     * @param {FootballPlay[]} rangePlays
     * @param {boolean} isTurnover
     * @param {PlayInsertionType} insertType
     * @returns {Promise<boolean>}
     * @private
     */
    async _isInvalidPossessionChange(
        newPlay,
        existingPlay,
        rangePlays,
        isTurnover,
        insertType
    ) {
        const { currentDriveId } = newPlay.contextSnapshot;

        if (insertType === PlayInsertionType.Replace) {
            const isOriginalTurnover = await this._containsTurnoverPlay([
                existingPlay
            ]);
            const lastDrive = await DriveService.getLastDrive(newPlay.gameId);
            const isTurnoverResultChanged = isOriginalTurnover !== isTurnover;
            const isLatestDrive = lastDrive?.id === currentDriveId;

            return isTurnoverResultChanged && !isLatestDrive;
        }

        const rangeContainsTurnover = await this._containsTurnoverPlay(
            rangePlays.filter((p) => p.index >= newPlay.index)
        );

        return isTurnover && rangeContainsTurnover;
    }

    /**
     * Validate that the provided play can be inserted and return the play (on validation success) along with the resulting gameContext (following the reprocessing of the provided play), an isTurnover flag, and the play currently existing at the insertion index.
     * @param {FootballPlay} play
     * @param {PlayInsertionType} insertType
     * @returns {Promise<{ play: FootballPlay | void, gameContext: FootballGameContext, isTurnover: boolean, existingPlay: FootballPlay }>}
     * @private
     */
    async _validateInsert(play, insertType) {
        const { statLogs, gameContext } = await this._reprocessPlay(play.id);
        const playAtIndex = await this._getPlayAtIndex(play.gameId, play.index);
        const rangeIndexes = await this._getReintegrationRange(playAtIndex);
        let rangePlays = (await this.getGamePlays(play.gameId)).filter((p) =>
            rangeIndexes.some((i) => i === p.index)
        );

        if (insertType === PlayInsertionType.Replace) {
            rangePlays = rangePlays.filter(
                (p) => p.index !== play.index || p.id === play.id
            );
        }

        const isTurnover =
            gameContext.homeTeamHasPossession !==
            play.contextSnapshot.homeTeamHasPossession;
        const isInvalidPossessionChange = await this._isInvalidPossessionChange(
            play,
            playAtIndex,
            rangePlays,
            isTurnover,
            insertType
        );

        return {
            play: await ValidationService.validateFootballPlayInsertion(
                play,
                playAtIndex,
                rangePlays,
                statLogs,
                isInvalidPossessionChange,
                insertType
            ),
            gameContext,
            isTurnover,
            existingPlay: playAtIndex
        };
    }

    /**
     * Determine if any of the provided plays result in a turnover.
     * @param {FootballPlay[]} plays
     * @returns {Promise<boolean> | boolean}
     * @private
     */
    async _containsTurnoverPlay(plays) {
        return plays.reduce(async (acc, play) => {
            const turnoverFound = await acc;
            if (turnoverFound) return turnoverFound;
            const { gameContext: context } = await this._reprocessPlay(play.id);
            return (
                context.homeTeamHasPossession !==
                play.contextSnapshot.homeTeamHasPossession
            );
        }, Promise.resolve(false));
    }

    /**
     * Update and return a Football Game Context after refreshing the participating players
     * @param {string} gameId
     * @returns {Promise<FootballGameContext>}
     */
    async refreshParticipants(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);
        const { awayTeamId, homeTeamId, gameConfig } = gameContext;
        const { participationStyle } = gameConfig.general;

        if (participationStyle === ParticipationStyle.Continuous.code) {
            const plays = await playRepository.getPlaysWithFragments(gameId);

            const fragments = plays.map(({ fragments }) => fragments).flat();

            const updatedParticipants = fragments.reduce((acc, fragment) => {
                const participants = [...acc];
                const requiredPlayerData = fragment.requiredData?.filter(
                    ({ type }) =>
                        [
                            FragmentDataType.Players,
                            FragmentDataType.Player
                        ].some((t) => t === type)
                );

                requiredPlayerData.forEach(({ name }) => {
                    if (fragment[name]) {
                        const players = isArray(fragment[name])
                            ? fragment[name]
                            : [fragment[name]];

                        players.forEach((player) => {
                            if (
                                !participants.some(({ id }) => id === player.id)
                            ) {
                                participants.push(player);
                            }
                        });
                    }
                });

                return [...participants];
            }, []);

            [awayTeamId, homeTeamId].forEach((teamId) => {
                gameContext.teamContexts[teamId].participants =
                    updatedParticipants
                        .filter((p) => p.teamId === teamId)
                        .map((p) => p.id);
            });

            return ContextService.updateContext(gameContext);
        }
        return gameContext;
    }

    /**
     * Determine if the game should prompt to change the offensive team
     * @param {string} gameId
     * @returns {Promise<boolean>}
     */
    async getPromptOffensiveTeam(gameId) {
        const gameContext = await ContextService.getGameContext(gameId);
        const isSecondHalf =
            secondHalfPeriodNumber(gameContext) === gameContext.currentPeriod;
        const isValidOT =
            gameContext.currentPeriod >
            gameContext.gameConfig.general.periodsInRegulation + 1;
        const isCfl = gameContext.ruleSetId === Ruleset.Cfl.code;
        return (isSecondHalf || isValidOT) && isCfl;
    }

    /**
     * Change the offensive team for a game
     * @param {string} gameId
     * @param {string} teamId
     * @returns {Promise<{play: FootballPlay, context: *}>}
     */
    async updateOffensiveTeam(gameId, teamId) {
        let gameContext = await ContextService.getGameContext(gameId);
        const { homeTeamId, gameConfig } = gameContext;
        const { kickoff: kickOffSpot, overtimeStartingYardline: otSpot } =
            gameConfig.spots;
        const { firstDownYards } = gameConfig.general;
        const isOT = isOvertimePeriod(
            gameContext.currentPeriod,
            gameContext.gameConfig.general.periodsInRegulation
        );
        const homeTeamHasPossession = homeTeamId === teamId;
        const KOSpot = isOT ? otSpot : kickOffSpot;
        const fieldPosition = homeTeamHasPossession
            ? KOSpot
            : gameContext.getFieldLength() - KOSpot;
        const firstDownAt = homeTeamHasPossession
            ? fieldPosition + firstDownYards
            : fieldPosition - firstDownYards;

        gameContext = await ContextService.updateContext(
            Object.assign(gameContext, {
                homeTeamHasPossession,
                fieldPosition,
                firstDownAt
            })
        );

        const { play } = await this.getOrCreatePlay(gameId);

        return {
            context: FootballSerializer.serializeGameContext(gameContext),
            play: play
        };
    }
}

export default new FootballService();
